import { useState, useMemo, useRef, useEffect } from "react";
import { useTheme, useNotify, makeStyles, LIGHT } from "./App";
import CardScanner from "./CardScanner";
import MarketIntelligence from "./MarketIntelligence";
import { QRScanner } from "./QRTools";
import CONFIG from "./config";
import { supabase, fetchAllMarketIntelReports, addBuyerPurchase, writeAuditLog, addTradePhoto, uploadCardImage, pkmnpricesSearch, pkmnpricesLookup, computeTax, removeDealBoardListingIfListed, getPriceHistoryView, unifiedCardSearch, unifiedCardLookup, computeConditionPrice, apitcgSearchSealed, apitcgLookupSealed } from "./supabase";
import { SHARED_MARKET_INTEL_VENDOR_ID, computeCardAnalytics, PHASE_META } from "./marketIntelLogic";
import { evaluateBuyDecision } from "./buyAdvisorLogic";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const fmt     = n => `$${(+n||0).toFixed(2)}`;
const fmtT    = n => `$${Math.round(+n||0)}`;
const minSell = cost => Math.max(1, Math.ceil(cost * 1.25));
const RATES   = [70, 75, 80, 85, 90, 100];
const GENDERS = ["Male","Female","Skip"];
const CONDITIONS = ["Mint","Near Mint","Lightly Played","Moderately Played","Heavily Played","Damaged"];
const PAYMENT_TYPES = ["Cash","Venmo","Cash App","Zelle","PayPal"];
const RARITIES   = ["Common","Uncommon","Rare","Holo Rare","Reverse Holo","Double Rare",
  "Ultra Rare","Illustration Rare","Special Illustration Rare","Secret Rare",
  "Hyper Rare","Gold Rare","Full Art","Alternate Art","Trainer Gallery","Galarian Gallery","Promo"];

export function PriceHistoryModal({ marketPriceInfo, condition, variant, cardName, isPaid = true, onClose, S, T }) {
  const isGraded = !!(marketPriceInfo?.gradingCompany && marketPriceInfo?.grade);

  if (isGraded) {
    const rows = marketPriceInfo?._rawRows || [];
    return (
      <div style={S.overlay} onClick={e=>{ if (e.target===e.currentTarget) onClose(); }}>
        <div style={{...S.drawer, maxWidth:600}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4}}>
            <div style={S.h2}>Price History</div>
            <button style={{...S.btn("ghost"), padding:"4px 10px"}} onClick={onClose}>✕</button>
          </div>
          <div style={{fontSize:12, color:T.textMuted, marginBottom:16}}>
            {cardName} · {marketPriceInfo.gradingCompany} {marketPriceInfo.grade}
          </div>
          <div style={{fontSize:13, fontWeight:700, marginBottom:10}}>eBay Sold Listings</div>
          {rows.length === 0 ? (
            <div style={{fontSize:13, color:T.textFaint, textAlign:"center", padding:20}}>
              No recent eBay sales found for this grade.
            </div>
          ) : (
            <div style={{maxHeight:400, overflowY:"auto"}}>
              {rows.map((r, i) => (
                <div key={i} style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr auto",
                  gap:8, alignItems:"center", padding:"8px 0",
                  borderTop: i>0 ? `1px solid ${T.borderSoft||T.border}` : "none", fontSize:12}}>
                  <span style={{color:T.textMuted}}>{r.date}</span>
                  <span style={{fontWeight:700}}>{fmt(r.price)}</span>
                  <span style={{color:T.textFaint}}>{r.grader || "—"}</span>
                  <span style={{color:T.textFaint}}>{r.grade || "—"}</span>
                  {r.url ? (
                    <a href={r.url} target="_blank" rel="noopener noreferrer"
                      style={{color:"#3C66EE", fontWeight:600, textDecoration:"none"}}>View ↗</a>
                  ) : <span />}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const view = getPriceHistoryView(marketPriceInfo?._priceData, condition, variant);
  const resolved = view?.periods?.["7d"];
  const chartData = resolved?.chart || [];
  const conditionTable = resolved?.conditions || {};

  return (
    <div style={S.overlay} onClick={e=>{ if (e.target===e.currentTarget) onClose(); }}>
      <div style={{...S.drawer, maxWidth:600}}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4}}>
          <div style={S.h2}>Price History</div>
          <button style={{...S.btn("ghost"), padding:"4px 10px"}} onClick={onClose}>✕</button>
        </div>
        <div style={{fontSize:12, color:T.textMuted, marginBottom:16}}>{cardName}</div>

        <div style={{fontSize:13, fontWeight:700, color:"#4ade80", marginBottom:12}}>
          {condition || "Near Mint"} · Last 7 days
        </div>

        {chartData.length === 0 ? (
          <div style={{fontSize:13, color:T.textFaint, textAlign:"center", padding:20}}>
            Not enough sales data for this condition yet.
          </div>
        ) : (
          <div style={{height:200, marginBottom:20}}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="priceHistoryFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4ade80" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#4ade80" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={T.border} vertical={false} />
                <XAxis dataKey="date" tick={{fontSize:10, fill:T.textFaint}} axisLine={false} tickLine={false} />
                <YAxis tick={{fontSize:10, fill:T.textFaint}} axisLine={false} tickLine={false}
                  tickFormatter={v => `$${v}`} width={50} />
                <Tooltip formatter={v => [`$${v}`, "Price"]}
                  contentStyle={{background:T.surface, border:`1px solid ${T.border}`, borderRadius:8, fontSize:12}} />
                <Area type="monotone" dataKey="price" stroke="#4ade80" strokeWidth={2}
                  fill="url(#priceHistoryFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        <div style={{fontSize:13, fontWeight:700, marginBottom:8}}>Market Prices</div>
        {Object.keys(conditionTable).length === 0 ? (
          <div style={{fontSize:12, color:T.textFaint}}>No condition breakdown available.</div>
        ) : (
          <div style={{position:"relative"}}>
            <div style={!isPaid ? {filter:"blur(6px)", userSelect:"none", pointerEvents:"none"} : undefined}>
              {Object.entries(conditionTable)
                .sort(([a], [b]) => {
                  const order = ["Near Mint", "Lightly Played", "Moderately Played", "Heavily Played", "Damaged"];
                  const ia = order.indexOf(a), ib = order.indexOf(b);
                  if (ia === -1 && ib === -1) return a.localeCompare(b);
                  if (ia === -1) return 1;
                  if (ib === -1) return -1;
                  return ia - ib;
                })
                .map(([c, price], i) => (
                <div key={c} style={{display:"flex", justifyContent:"space-between", alignItems:"center",
                  padding:"7px 0", borderTop: i>0 ? `1px solid ${T.borderSoft||T.border}` : "none"}}>
                  <span style={{fontSize:13, fontWeight: c===(condition||"Near Mint") ? 700 : 400,
                    color: c===(condition||"Near Mint") ? "#4ade80" : T.textMuted}}>{c}</span>
                  <span style={{fontSize:13, fontWeight:700}}>{fmt(price)}</span>
                </div>
              ))}
            </div>
            {!isPaid && (
              <div style={{position:"absolute", inset:0, display:"flex", flexDirection:"column",
                alignItems:"center", justifyContent:"center", gap:6, textAlign:"center"}}>
                <div style={{fontSize:12, fontWeight:700, color:T.text,
                  background:T.surface, padding:"4px 10px", borderRadius:6,
                  border:`1px solid ${T.border}`}}>
                  🔒 Upgrade to Pro to see prices by condition
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const SHOW_MAX_W = 720;
const centerPad = (max, side = 0) => `0 max(${side}px, calc((100% - ${max}px) / 2))`;

export default function ShowMode({ show, inventory, showItems, onExit, onUpdateItem, onAddInventoryItem, onAddShowItem, onRemoveShowItem, onUnlinkShowItem, onAddTrade, buyers = [], isPaid = true, vendorId, vendor, userEmail }) {
  const { S, T } = useTheme();
  const { openUpgradeModal } = useNotify();
  const [tab, setTab]               = useState("sell");
  const [showReport, setShowReport] = useState(false);
  const [dayMode, setDayMode]       = useState(false);
  const [quickScanActive] = useState(false);
  const dayTheme = useMemo(() => {
    const t = { ...LIGHT, bg:"#f5f5f0", surface:"#ffffff", surface2:"#eeeee8", border:"#c4c4bc", borderSoft:"#d8d8d0",
      text:"#111111", textMuted:"#3a3a3a", textDim:"#4d4d4d", textFaint:"#5c5c5c", textTiny:"#6b6b6b" };
    return { S: makeStyles(t, false), T: t };
  }, []);
  const nightT = useMemo(() => ({ ...T, textFaint: T.textDim }), [T]);
  const tabS = dayMode ? dayTheme.S : S;
  const tabT = dayMode ? dayTheme.T : nightT;
  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

  const showCardIds = useMemo(() =>
    (showItems||[]).filter(si => si.ShowID === show.ID).map(si => si.InventoryID),
  [showItems, show.ID]);

  const allInventory = useMemo(() =>
    inventory.filter(i => i.Status === "In Stock" || i.Status === "Reserved"),
  [inventory]);


  const setNames = useMemo(() => {
    const sets = [...new Set(inventory.map(i => i.Set).filter(Boolean))].sort();
    return sets;
  }, [inventory]);

  const navStyle = (active) => ({
    flex:1, padding:"10px 0", border:"none", borderRadius:0,
    background: active ? "#2563eb" : T.surface2,
    color: active ? "#ffffff" : T.textMuted,
    fontFamily:"'Sora',sans-serif", fontSize:14, fontWeight: active ? 700 : 500,
    cursor:"pointer", borderBottom: active ? "none" : `1px solid ${T.border}`,
  });

  const showCards = useMemo(() =>
    inventory.filter(i =>
      i.Status !== "Sold" &&
      (showCardIds.includes(i.ID) ||
      (i.ShowID === show.ID && i.Source !== "Individual Buy"))
    ),
  [inventory, show.ID, showCardIds]);

  const showTotalValue = useMemo(() =>
    showCards.reduce((s,i) => s + (parseFloat(i.ListPrice)||0), 0),
  [showCards]);

  if (showReport) return (
    <ShowReport show={show} inventory={inventory} showTotalValue={showTotalValue}
      onBack={()=>setShowReport(false)} S={S} T={T} />
  );

  return (
    <div style={{...S.app, display:"flex", flexDirection:"column", height:"100dvh", minHeight:0}}>
      {!quickScanActive && (
      <div style={{background:"#1e2d3d", padding:"10px 16px", display:"flex",
        alignItems:"center", justifyContent:"space-between", flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <img src="/cv-logo_dark.png" alt="VS" style={{height:28,objectFit:"contain"}} />
          <div>
            <div style={{fontFamily:"'DM Mono',monospace", fontSize:11, color:"#88bdf2",
              textTransform:"uppercase", letterSpacing:"0.08em"}}>Show Mode</div>
            <div style={{fontWeight:700, fontSize:15, color:"#e8f0f8"}}>{show.Name}</div>
          </div>
        </div>
        <div style={{display:"flex", gap:8}}>
          <button style={{padding:"7px 12px", borderRadius:8,
            border:`1px solid ${dayMode ? "#6a89a7" : "#88bdf2"}`,
            background: dayMode ? "rgba(255,255,255,0.15)" : "transparent",
            color: dayMode ? "#e8f0f8" : "#88bdf2",
            fontFamily:"'Sora',sans-serif", fontSize:12, fontWeight:600, cursor:"pointer"}}
            onClick={()=>setDayMode(d=>!d)}>
            {dayMode ? "🌙 Night" : "☀️ Day"}
          </button>
          <button style={{padding:"7px 12px", borderRadius:8, border:"1px solid #88bdf2",
            background:"transparent", color:"#88bdf2", fontFamily:"'Sora',sans-serif",
            fontSize:12, fontWeight:600, cursor:"pointer"}}
            onClick={()=>setShowReport(true)}>
            Report
          </button>
          <button style={{padding:"7px 12px", borderRadius:8, border:"1px solid #6a89a7",
            background:"transparent", color:"#e8f0f8", fontFamily:"'Sora',sans-serif",
            fontSize:13, fontWeight:600, cursor:"pointer"}}
            onClick={onExit}>
            Exit
          </button>
        </div>
      </div>
      )}

      {!quickScanActive && (
      <div style={{borderBottom:`1px solid ${T.border}`, flexShrink:0, background:T.surface2}}>
      <div style={{display:"flex", maxWidth:SHOW_MAX_W, margin:"0 auto", width:"100%"}}>
        {["sell","trade","buy"].map(t=>(
          <button key={t} style={navStyle(tab===t)} onClick={()=>setTab(t)}>
            {t==="sell" ? "💰 Sell" : t==="trade" ? "🔄 Trade" : "🛒 Buy"}
            {t==="trade" && !isPaid && (
              <span style={{fontSize:9,fontWeight:700,color:"#FF9600",
                background:"rgba(255,150,0,0.12)",border:"0.5px solid rgba(255,150,0,0.4)",
                borderRadius:4,padding:"1px 4px",marginLeft:5}}>PRO</span>
            )}
          </button>
        ))}
      </div>
      </div>
      )}

      <div style={{flex:1, overflowY:"auto", WebkitOverflowScrolling:"touch", overflowX:"hidden",
        padding:centerPad(SHOW_MAX_W), boxSizing:"border-box",
        background: dayMode ? "#f5f5f0" : "transparent"}}>
        {tab==="sell"  && <SellTab  show={show} showCards={showCards} allInventory={allInventory}
          today={today} onUpdateItem={onUpdateItem} onRemoveShowItem={onRemoveShowItem} onUnlinkShowItem={onUnlinkShowItem}
          buyers={buyers} isPaid={isPaid} vendorId={vendorId} vendor={vendor} userEmail={userEmail} dayMode={dayMode} S={tabS} T={tabT} />}
        {tab==="trade" && (isPaid
          ? <TradeTab show={show} showCards={showCards} allInventory={allInventory}
              today={today} setNames={setNames} vendorId={vendorId}
              onUpdateItem={onUpdateItem} onAddInventoryItem={onAddInventoryItem}
              onRemoveShowItem={onRemoveShowItem} onUnlinkShowItem={onUnlinkShowItem}
              onAddShowItem={onAddShowItem}
              onAddTrade={onAddTrade} dayMode={dayMode} S={tabS} T={tabT} />
          : <div style={{padding:24}}>
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",
                textAlign:"center",gap:12,paddingTop:16}}>
                <div style={{fontSize:24}}>🔒</div>
                <div style={{fontSize:15,fontWeight:600}}>Paid Feature</div>
                <div style={{fontSize:13,color:T.textMuted,maxWidth:280,lineHeight:1.6}}>
                  The Trade tab is available on the Pro plan.
                </div>
                <button onClick={openUpgradeModal}
                  style={{...S.btn("ghost"),fontSize:13}}>
                  Upgrade to Pro →
                </button>
              </div>
            </div>
        )}
        {tab==="buy"   && <BuyTab   show={show} today={today} setNames={setNames}
          allInventory={allInventory} vendorId={vendorId} isPaid={isPaid}
                    onAddInventoryItem={onAddInventoryItem} onAddShowItem={async(cardId)=>{
            if (onAddShowItem) await onAddShowItem(show.ID, cardId);
          }} dayMode={dayMode} S={tabS} T={tabT} />}
      </div>
    </div>
  );
}

// ─── SHOW REPORT ──────────────────────────────────────────────────────────────
function ShowReport({ show, inventory, showTotalValue, onBack, S, T }) {
  const showSold      = useMemo(() =>
    inventory.filter(i => i.ShowID === show.ID && i.Status === "Sold" && i.SoldChannel !== "Trade"),
  [inventory, show.ID]);
  const showBought     = useMemo(() =>
    inventory.filter(i => i.AcquiredShowID === show.ID && i.Source !== "Customer Trade"),
  [inventory, show.ID]);
  const showTradesIn   = useMemo(() =>
    inventory.filter(i => i.AcquiredShowID === show.ID && i.Source === "Customer Trade"),
  [inventory, show.ID]);
  const showTradedOut  = useMemo(() =>
    inventory.filter(i => i.ShowID === show.ID && i.Status === "Sold" && i.SoldChannel === "Trade"),
  [inventory, show.ID]);

  const kpisFor = (sold, bought, tradesIn, tradedOut) => {
    const revenue    = sold.reduce((s,i)=>s+(i.SoldPrice||0),0);
    const soldCost   = sold.reduce((s,i)=>s+(i.AcquisitionCost||0),0);
    const tradeGains = tradedOut.reduce((s,i)=>s+(i.TradeGain||0),0);
    return {
      revenue, profit: revenue - soldCost + tradeGains,
      sold: sold.length, bought: bought.length,
      tradesIn: tradesIn.length, tradedOut: tradedOut.length,
    };
  };

  const grandTotals = kpisFor(showSold, showBought, showTradesIn, showTradedOut);

  const showDays = useMemo(() => {
    if (!show.StartDate) return [];
    const start = new Date(show.StartDate + "T00:00:00");
    const end   = show.EndDate ? new Date(show.EndDate + "T00:00:00") : start;
    const list  = [];
    const pad   = n => String(n).padStart(2, "0");
    for (let d = new Date(start); d <= end; d.setDate(d.getDate()+1)) {
      list.push({
        date: `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`,
        label: d.toLocaleDateString("en-US", { weekday: "long" }),
      });
    }
    return list;
  }, [show.StartDate, show.EndDate]);

  const isMultiDay = showDays.length > 1;

  const dayKpis = (dateStr) => kpisFor(
    showSold.filter(i => i.SoldDate === dateStr),
    showBought.filter(i => i.AcquisitionDate === dateStr),
    showTradesIn.filter(i => i.AcquisitionDate === dateStr),
    showTradedOut.filter(i => i.SoldDate === dateStr),
  );

  const KpiGrid = ({ kpis }) => (
    <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:20}}>
      <div style={S.stat}><div style={S.statLbl}>Revenue</div><div style={{...S.statVal,color:"#4ade80"}}>{fmtT(kpis.revenue)}</div></div>
      <div style={S.stat}><div style={S.statLbl}>Profit</div><div style={{...S.statVal,color:kpis.profit>=0?"#4ade80":"#f87171"}}>{fmtT(kpis.profit)}</div></div>
      <div style={S.stat}><div style={S.statLbl}># of Items Sold</div><div style={{...S.statVal,color:"#4ade80"}}>{kpis.sold}</div></div>
      <div style={S.stat}><div style={S.statLbl}># of Items Purchased</div><div style={S.statVal}>{kpis.bought}</div></div>
      <div style={S.stat}><div style={S.statLbl}># of Items Traded Out</div><div style={S.statVal}>{kpis.tradedOut}</div></div>
      <div style={S.stat}><div style={S.statLbl}># of Items Traded In</div><div style={S.statVal}>{kpis.tradesIn}</div></div>
    </div>
  );

  return (
    <div style={{...S.app, padding:`16px max(16px, calc((100% - 900px) / 2))`, maxWidth:"100%", boxSizing:"border-box"}}>
      <div style={{display:"flex", alignItems:"center", gap:12, marginBottom:20}}>
        <button style={{...S.btn("ghost"), padding:"6px 10px"}} onClick={onBack}>← Back</button>
        <div style={{fontSize:16, fontWeight:600}}>{show.Name} — Report</div>
      </div>

      {showTotalValue > 0 && (
        <div style={{...S.stat, background:"rgba(60,102,238,0.08)", borderColor:"rgba(60,102,238,0.25)", marginBottom:16}}>
          <div style={S.statLbl}>Remaining Show Value</div>
          <div style={{...S.statVal, color:"#3C66EE"}}>{fmtT(showTotalValue)}</div>
        </div>
      )}

      <div style={{fontSize:12, fontWeight:700, color:T.textMuted, marginBottom:8, textTransform:"uppercase", letterSpacing:".05em"}}>
        Show Grand Totals
      </div>
      <KpiGrid kpis={grandTotals} />

      {isMultiDay && showDays.map(d => (
        <div key={d.date}>
          <div style={{fontSize:12, fontWeight:700, color:T.textMuted, marginBottom:8, textTransform:"uppercase", letterSpacing:".05em"}}>
            {d.label}
          </div>
          <KpiGrid kpis={dayKpis(d.date)} />
        </div>
      ))}

      {grandTotals.sold===0 && grandTotals.bought===0 && grandTotals.tradesIn===0 && grandTotals.tradedOut===0 && (
        <div style={{textAlign:"center", padding:"40px 0", color:T.textFaint}}>
          No transactions recorded yet
        </div>
      )}
    </div>
  );
}

// ─── SELL TAB ─────────────────────────────────────────────────────────────────
function SellTab({ show, showCards, allInventory, today, onUpdateItem, onRemoveShowItem, onUnlinkShowItem, buyers = [], isPaid = true, vendorId, vendor, userEmail, dayMode, S, T }) {
  const { toast } = useNotify();
  const [mode, setMode]           = useState("single");
  const [search, setSearch]       = useState("");
  const [sort, setSort]           = useState("asc");
  const [visibleCount, setVisibleCount] = useState(40);
  const [selected, setSelected]   = useState(null);
  const [price, setPrice]         = useState("");
  const [gender, setGender]       = useState("Skip");
  const [buyerSearch, setBuyerSearch] = useState("");
  const [attachedBuyer, setAttachedBuyer] = useState(null);
  const [cart, setCart]           = useState([]);
  const [multiPayment, setMultiPayment] = useState("Cash");
  const [multiDiscountPct, setMultiDiscountPct] = useState(0);
  const [singlePayment, setSinglePayment] = useState("Cash");
  const [singleDiscountPct, setSingleDiscountPct] = useState(0);
  const [saving, setSaving]       = useState(false);
  const [lastSold, setLastSold]   = useState(null);
  const [scanning, setScanning]   = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [savingPrice, setSavingPrice] = useState(false);

  const filteredBuyers = useMemo(() => {
    if (!buyerSearch.trim()) return buyers.slice(0, 6);
    const q = buyerSearch.toLowerCase();
    return buyers.filter(b =>
      b.FullName.toLowerCase().includes(q) ||
      (b.Phone||"").includes(q)
    ).slice(0, 6);
  }, [buyers, buyerSearch]);

  const results = useMemo(()=>{
    const q = search.trim().toLowerCase();
    const filtered = q
      ? showCards.filter(i =>
          i.CardName?.toLowerCase().includes(q) ||
          i.Set?.toLowerCase().includes(q) ||
          i.Number?.toLowerCase().includes(q))
      : showCards;
    return [...filtered].sort((a,b)=>{
      const na=(a.CardName||"").toLowerCase(), nb=(b.CardName||"").toLowerCase();
      return sort==="asc" ? na.localeCompare(nb) : nb.localeCompare(na);
    });
  }, [search, showCards, sort]);

  const cartTotal = cart.reduce((s,c)=>s+(parseFloat(c._price)||parseFloat(c.ListPrice)||0),0);
  const multiDiscountedTotal = cartTotal * (1 - multiDiscountPct/100);

  const selectCard = (item, fromScan = false) => {
    if (mode === "multi") {
      setCart(p => {
        const already = p.find(x=>x.ID===item.ID);
        if (fromScan) {
          toast(already ? `Removed: ${item.CardName}` : `Added: ${item.CardName} — $${Math.round(item.ListPrice||0)}`, "success");
        }
        return already ? p.filter(x=>x.ID!==item.ID) : [...p,{...item,_price:String(Math.round(item.ListPrice||0))}];
      });
    } else {
      setSelected(item);
      setPrice(item.ListPrice ? String(Math.round(item.ListPrice)) : "");
      setSearch("");
    }
  };

  const priceChanged = !!(selected && price && parseFloat(price) !== (parseFloat(selected.ListPrice) || 0));

  const saveShowPriceOnly = async () => {
    if (!selected || !price) return;
    setSavingPrice(true);
    const newPrice = parseFloat(price);
    const oldPrice = parseFloat(selected.ListPrice) || 0;
    const frozenOnlinePrice = selected.OnlinePrice != null ? selected.OnlinePrice : oldPrice;
    try {
      await onUpdateItem(selected.ID, { ListPrice: newPrice, OnlinePrice: frozenOnlinePrice });
      if (vendorId) {
        writeAuditLog(vendorId, userEmail || "show-mode", "inventory.show_price_updated",
          selected.ID, selected.CardName, `$${oldPrice}`, `Show: $${newPrice} · Online frozen at $${frozenOnlinePrice}`);
      }
      setSelected(s => s ? {...s, ListPrice:newPrice, OnlinePrice:frozenOnlinePrice} : s);
      toast(`Show price updated to $${newPrice} — online price unchanged`, "success");
      setShowPriceModal(false);
    } catch(e) {
      toast("Failed to save price: " + e.message, "error");
    }
    setSavingPrice(false);
  };

  const saveBothPrices = async () => {
    if (!selected || !price) return;
    setSavingPrice(true);
    const newPrice = parseFloat(price);
    const oldPrice = parseFloat(selected.ListPrice) || 0;
    try {
      await onUpdateItem(selected.ID, { ListPrice: newPrice, OnlinePrice: newPrice });
      if (vendorId) {
        writeAuditLog(vendorId, userEmail || "show-mode", "inventory.price_updated",
          selected.ID, selected.CardName, `$${oldPrice}`, `$${newPrice} (show + online)`);
      }
      setSelected(s => s ? {...s, ListPrice:newPrice, OnlinePrice:newPrice} : s);
      toast(`Price updated to $${newPrice} everywhere`, "success");
      setShowPriceModal(false);
    } catch(e) {
      toast("Failed to save price: " + e.message, "error");
    }
    setSavingPrice(false);
  };

  const confirmSingle = async () => {
    if (!selected || !price) return;
    setSaving(true);
    const discountMult = 1 - singleDiscountPct/100;
    const finalPrice = Math.round(parseFloat(price) * discountMult * 100) / 100;
    const { TaxRate, TaxAmount } = computeTax(finalPrice, vendor);
    await onUpdateItem(selected.ID, {
      Status:"Sold", SoldPrice:finalPrice, SoldDate:today, SoldAt:new Date().toISOString(),
      SoldChannel:"Local Show", BuyerGender:gender==="Skip"?"":gender,
      PaymentMethod: singlePayment,
      ShowID:show.ID, ShowName:show.Name, TaxRate, TaxAmount,
      Notes: singleDiscountPct > 0
        ? [selected.Notes, `${singleDiscountPct}% bulk order discount applied`].filter(Boolean).join(" · ")
        : selected.Notes,
    });
    removeDealBoardListingIfListed(selected.ID);
    if (onUnlinkShowItem) await onUnlinkShowItem(show.ID, selected.ID);
    else if (onRemoveShowItem) await onRemoveShowItem(show.ID, selected.ID).catch(()=>{});
    if (attachedBuyer) {
      try {
        await addBuyerPurchase(attachedBuyer.ID, {
          inventoryId: selected.ID,
          soldPrice:   finalPrice,
          showId:      show.ID,
        });
        toast(`Sale attached to ${attachedBuyer.FullName}`, "success");
      } catch(e) {
        toast("Sale recorded but couldn't attach to buyer: " + e.message, "alert");
      }
    }
    setLastSold({name:selected.CardName, price:finalPrice, count:1});
    setSelected(null); setPrice(""); setGender("Skip"); setSingleDiscountPct(0);
    setAttachedBuyer(null); setBuyerSearch("");
    setSaving(false);
  };

  const confirmMulti = async () => {
    if (cart.length === 0) return;
    setSaving(true);
    const discountMult = 1 - multiDiscountPct/100;
    for (const card of cart) {
      const basePrice = parseFloat(card._price)||parseFloat(card.ListPrice)||0;
      const finalPrice = Math.round(basePrice * discountMult * 100) / 100;
      const { TaxRate, TaxAmount } = computeTax(finalPrice, vendor);
      await onUpdateItem(card.ID, {
        Status:"Sold", SoldPrice:finalPrice, TaxRate, TaxAmount,
        SoldDate:today, SoldAt:new Date().toISOString(), SoldChannel:"Local Show", BuyerGender:"",
        PaymentMethod: multiPayment,
        ShowID:show.ID, ShowName:show.Name,
        Notes: multiDiscountPct > 0
          ? [card.Notes, `${multiDiscountPct}% bulk order discount applied`].filter(Boolean).join(" · ")
          : card.Notes,
      });
      removeDealBoardListingIfListed(card.ID);
      if (onUnlinkShowItem) await onUnlinkShowItem(show.ID, card.ID);
      else if (onRemoveShowItem) await onRemoveShowItem(show.ID, card.ID).catch(()=>{});
    }
    setLastSold({name:`${cart.length} cards`, price:multiDiscountedTotal.toFixed(0), count:cart.length});
    setCart([]); setMultiDiscountPct(0); setSaving(false);
  };

  const P = dayMode
    ? {blue:"#1d4ed8", green:"#15803d", red:"#b91c1c", amber:"#92400e", ok:"#4d7c0f"}
    : {blue:"#88bdf2", green:"#4ade80", red:"#f87171", amber:"#fbbf24", ok:"#7EAF5C"};

  return (
    <div style={{padding:16, maxWidth:"100%", boxSizing:"border-box",
      background: dayMode ? "#f5f5f0" : "transparent", color: T.text, minHeight:"100%"}}>

      {lastSold && !selected && cart.length===0 && (
        <div style={{background:dayMode?"#dcfce7":"#0f2e1a",border:`1px solid ${dayMode?"#86efac":"#166534"}`,borderRadius:10,
          padding:"10px 14px",marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:13,color:P.green}}>✓ Sold {lastSold.name}</span>
          <span style={{fontSize:13,color:P.green,fontWeight:700}}>${lastSold.price}</span>
        </div>
      )}

      {!selected && (
        <div style={{display:"flex",gap:8,marginBottom:12}}>
          {[["single","Single Sale"],["multi",`Multi Sale${cart.length>0?` (${cart.length})`:""}`]].map(([m,label])=>(
            <button key={m}
              style={{flex:1, padding:"10px 0", fontSize:14, fontWeight:600, borderRadius:10,
                border:`1px solid ${mode===m?"#3C66EE":T.border}`,
                background: mode===m ? "#3C66EE" : "transparent",
                color: mode===m ? "#fff" : T.textMuted, cursor:"pointer"}}
              onClick={()=>{setMode(m); if(m==="single") setCart([]); else setSelected(null);}}>
              {label}
            </button>
          ))}
        </div>
      )}

      {mode==="multi" && cart.length>0 && (
        <div style={{...S.infoBox,marginBottom:10,borderColor:"#1d4ed8"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div style={{fontSize:15,fontWeight:700}}>{cart.length} cards selected</div>
            <div style={{textAlign:"right"}}>
              {multiDiscountPct > 0 && (
                <div style={{fontSize:12,color:T.textFaint,textDecoration:"line-through"}}>{fmtT(cartTotal)}</div>
              )}
              <div style={{fontSize:18,fontWeight:700,color:P.green}}>{fmtT(multiDiscountedTotal)}</div>
            </div>
          </div>
          {cart.map(c=>(
            <div key={c.ID} style={{display:"flex",alignItems:"center",gap:8,
              padding:"6px 0",borderTop:`0.5px solid ${T.border}`}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:14,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {c.CardName}
                </div>
                <div style={{fontSize:11,color:T.textFaint}}>
                  Cost: {fmtT(c.AcquisitionCost||0)}
                </div>
              </div>
              <input style={{...S.input,width:78,fontSize:15,textAlign:"right",padding:"6px 8px",fontWeight:600,flexShrink:0}}
                type="number" value={c._price}
                onChange={e=>setCart(p=>p.map(x=>x.ID===c.ID?{...x,_price:e.target.value}:x))}
                placeholder={String(c.ListPrice||"")} />
              <span style={{cursor:"pointer",color:T.textMuted,fontSize:18,flexShrink:0}}
                onClick={()=>setCart(p=>p.filter(x=>x.ID!==c.ID))}>×</span>
            </div>
          ))}
          <div style={{marginTop:12}}>
            <label style={{display:"block",fontSize:12,fontWeight:600,color:T.textMuted,marginBottom:6}}>
              Bulk Order Discount
            </label>
            <select style={{...S.input,marginBottom:10}}
              value={multiDiscountPct}
              onChange={e=>setMultiDiscountPct(parseInt(e.target.value,10))}>
              <option value={0}>No discount</option>
              {Array.from({length:19},(_, i)=>(i+1)*5).map(pct=>(
                <option key={pct} value={pct}>{pct}% off entire order</option>
              ))}
            </select>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
              {PAYMENT_TYPES.map(pt=>(
                <button key={pt}
                  style={{flex:1, padding:"9px 4px", fontSize:14, fontWeight:600, borderRadius:10,
                    border:`1px solid ${multiPayment===pt?"#3C66EE":T.border}`,
                    background: multiPayment===pt ? "#3C66EE" : "transparent",
                    color: multiPayment===pt ? "#fff" : T.textMuted, cursor:"pointer", minWidth:56}}
                  onClick={()=>setMultiPayment(pt)}>{pt}</button>
              ))}
            </div>
            <button style={{background:"#3C66EE",border:"none",borderRadius:12,
              color:"#fff",fontSize:16,fontWeight:700,padding:"14px",width:"100%",cursor:"pointer"}}
              onClick={confirmMulti} disabled={saving}>
              {saving?"Saving…":`Confirm ${cart.length} Sales · ${fmtT(multiDiscountedTotal)}`}
            </button>
          </div>
        </div>
      )}

      {selected && mode==="single" ? (
        <>
          {selected.IsConsignment && (
            <div style={{
              background:"rgba(245,158,11,0.12)", border:"1px solid rgba(245,158,11,0.4)",
              borderRadius:10, padding:"10px 14px", marginBottom:12,
            }}>
              <div style={{fontSize:13, fontWeight:800, color:P.amber, marginBottom:4}}>
                💎 CONSIGNMENT CARD
              </div>
              <div style={{fontSize:12, color:T.textMuted, display:"flex", gap:14, flexWrap:"wrap"}}>
                {selected.MinAcceptPrice > 0 ? (
                  <span>Min accept: <strong style={{color:P.amber}}>${selected.MinAcceptPrice}</strong></span>
                ) : (
                  <span>No minimum set</span>
                )}
                <span>Trades: <strong style={{color:selected.TradesAccepted?P.green:P.red}}>
                  {selected.TradesAccepted ? "Accepted" : "Not accepted"}
                </strong></span>
              </div>
            </div>
          )}
          <div style={{...S.card,marginBottom:16}}>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
              {selected.ImageURL ? (
                <img src={selected.ImageURL} alt="" style={{width:60,height:84,objectFit:"cover",borderRadius:8,flexShrink:0}} />
              ) : (
                <div style={{width:60,height:84,borderRadius:8,flexShrink:0,border:`1px dashed ${T.border}`,
                  display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,color:T.textFaint}}>⬡</div>
              )}
              <div style={{flex:1}}>
                <div style={{fontWeight:700,fontSize:17}}>{selected.CardName}</div>
                <div style={{fontSize:13,color:T.textMuted}}>{[selected.Set,selected.Condition].filter(Boolean).join(" · ")}</div>
                <div style={{fontSize:13,fontWeight:700,color:T.textFaint,marginTop:2}}>
                  List: {fmtT(selected.ListPrice)} · Min: ${selected.MinSellPrice||"—"}
                  {selected.OnlinePrice != null && Math.round(selected.OnlinePrice) !== Math.round(selected.ListPrice) && (
                    <span style={{color:P.amber}}> · Online: {fmtT(selected.OnlinePrice)}</span>
                  )}
                </div>
                <div style={{fontSize:16,fontWeight:700,marginTop:4,display:"flex",gap:12,flexWrap:"wrap"}}>
                  <span style={{color:P.red}}>Cost: {selected.AcquisitionCost ? fmt(selected.AcquisitionCost) : "N/A"}</span>
                  {selected.MarketValueAtAcquisition > 0 && (
                    <span style={{color:P.blue}}>Mkt at buy: {fmt(selected.MarketValueAtAcquisition)}</span>
                  )}
                  {selected.MarketPrice > 0 && (() => {
                    const mins = selected.MarketPriceUpdatedAt
                      ? Math.floor((Date.now() - new Date(selected.MarketPriceUpdatedAt).getTime()) / 60000) : null;
                    const ageLabel = mins === null ? null
                      : mins < 60   ? `${mins}m ago`
                      : mins < 1440 ? `${Math.floor(mins/60)}h ago`
                      : `${Math.floor(mins/1440)}d ago`;
                    const ageColor = mins === null ? (dayMode ? "#475569" : "#8899bb")
                      : mins < 60   ? P.ok
                      : mins < 720  ? (dayMode ? "#b45309" : "#FF9600")
                      : (dayMode ? "#991b1b" : "#AE1A21");
                    return (
                      <span style={{color:P.green, display:"flex", alignItems:"center", gap:5}}>
                        Reference: {fmt(selected.MarketPrice)}
                        {ageLabel && (
                          <span style={{
                            fontSize:12, fontWeight:700, color:ageColor,
                            background:`${ageColor}18`,
                            border:`0.5px solid ${ageColor}55`,
                            borderRadius:4, padding:"1px 5px",
                          }}>{ageLabel}</span>
                        )}
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>

            <div style={{marginBottom:12}}>
              <label style={{fontSize:13, fontWeight:600, color:T.textMuted,
                display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.05em"}}>
                Sale Price ($)
              </label>
              <input style={{...S.input,fontSize:24,fontWeight:700,textAlign:"center",padding:"14px",
                color:P.blue,width:160,maxWidth:"100%",margin:"0 auto",display:"block"}}
                type="number" value={price} onChange={e=>setPrice(e.target.value)} placeholder="0" />
            </div>

            {price && selected.AcquisitionCost > 0 && (()=>{
              const saleP=parseFloat(price)||0, cost=parseFloat(selected.AcquisitionCost)||0;
              const profit=saleP-cost, roiPct=cost>0?(profit/cost*100):0;
              const belowMin=selected.MinSellPrice>0&&saleP<selected.MinSellPrice;
              return (
                <div style={{display:"flex",gap:8,marginBottom:10,padding:"8px 12px",borderRadius:8,
                  background:profit>=0?"rgba(74,222,128,0.08)":"rgba(248,113,113,0.08)",
                  border:`1px solid ${profit>=0?"#166534":"#7f1d1d"}`}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:10,color:T.textFaint}}>Profit / Loss</div>
                    <div style={{fontSize:15,fontWeight:700,color:profit>=0?P.green:P.red,fontFamily:"'DM Mono',monospace"}}>
                      {profit>=0?"+":""}{fmt(profit)}
                    </div>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:10,color:T.textFaint}}>ROI</div>
                    <div style={{fontSize:15,fontWeight:700,color:profit>=0?P.green:P.red,fontFamily:"'DM Mono',monospace"}}>
                      {roiPct>=0?"+":""}{roiPct.toFixed(0)}%
                    </div>
                  </div>
                  {belowMin && <div style={{flex:1,display:"flex",alignItems:"center"}}>
                    <span style={{fontSize:11,color:P.red,fontWeight:600}}>⚠ Below min</span>
                  </div>}
                </div>
              );
            })()}

            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:12}}>
              {[selected.ListPrice,selected.MinSellPrice].filter(Boolean).map((p,i)=>(
                <button key={i}
                  style={{padding:"9px 16px", fontSize:14, fontWeight:600, borderRadius:10,
                    border:`1px solid ${T.border}`, background:"transparent",
                    color:T.textMuted, cursor:"pointer"}}
                  onClick={()=>setPrice(String(Math.round(p)))}>
                  {i===0?`List ${fmtT(p)}`:`Min ${fmtT(p)}`}
                </button>
              ))}
            </div>

            {priceChanged && (
              <button
                onClick={()=>setShowPriceModal(true)}
                style={{width:"100%", padding:"10px", marginBottom:12, borderRadius:10,
                  border:"1px solid #3C66EE", background:"rgba(60,102,238,0.1)",
                  color:P.blue, fontSize:13, fontWeight:700, cursor:"pointer"}}>
                💾 Save Price (${Math.round(parseFloat(selected.ListPrice)||0)} → ${Math.round(parseFloat(price)||0)})
              </button>
            )}

            <div style={{marginBottom:16}}>
              <label style={{fontSize:13, fontWeight:600, color:T.textMuted,
                display:"block", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.05em"}}>Buyer</label>
              <div style={{display:"flex",gap:6}}>
                {GENDERS.map(g=>(
                  <button key={g}
                    style={{flex:1, padding:"9px 0", fontSize:14, fontWeight:600, borderRadius:10,
                      border:`1px solid ${gender===g?"#3C66EE":T.border}`,
                      background: gender===g ? "#3C66EE" : "transparent",
                      color: gender===g ? "#fff" : T.textMuted, cursor:"pointer"}}
                    onClick={()=>setGender(g)}>{g}</button>
                ))}
              </div>
            </div>

            {isPaid && (
            <div style={{marginBottom:12}}>
              <label style={S.label}>
                Attach to Buyer
                <span style={{fontWeight:400, color:T.textFaint, marginLeft:4}}>(optional)</span>
              </label>
              {attachedBuyer ? (
                <div style={{
                  display:"flex", justifyContent:"space-between", alignItems:"center",
                  padding:"8px 12px", background:"rgba(126,175,92,0.1)",
                  border:"0.5px solid rgba(126,175,92,0.4)", borderRadius:8,
                }}>
                  <div>
                    <div style={{fontSize:13, fontWeight:600, color:P.ok}}>{attachedBuyer.FullName}</div>
                    {attachedBuyer.Phone && <div style={{fontSize:11, color:T.textFaint}}>{attachedBuyer.Phone}</div>}
                  </div>
                  <button onClick={()=>{setAttachedBuyer(null);setBuyerSearch("");}}
                    style={{background:"none",border:"none",color:T.textFaint,fontSize:16,cursor:"pointer"}}>×</button>
                </div>
              ) : (
                <>
                  <input style={{...S.input, marginBottom:buyers.length ? 6 : 0}}
                    placeholder="Search buyers by name or phone…"
                    value={buyerSearch}
                    onChange={e=>setBuyerSearch(e.target.value)}
                  />
                  {buyerSearch.trim() && filteredBuyers.length > 0 && (
                    <div style={{
                      background:T.surface2, borderRadius:8, overflow:"hidden",
                      border:`0.5px solid ${T.border||"rgba(255,255,255,0.08)"}`,
                    }}>
                      {filteredBuyers.map(b => (
                        <button key={b.ID} onClick={()=>{setAttachedBuyer(b);setBuyerSearch("");}}
                          style={{
                            display:"block", width:"100%", textAlign:"left",
                            padding:"9px 12px", background:"none", border:"none",
                            borderBottom:`0.5px solid ${T.border||"rgba(255,255,255,0.06)"}`,
                            cursor:"pointer", color:T.text,
                          }}>
                          <div style={{fontSize:13, fontWeight:600}}>{b.FullName}</div>
                          {b.Phone && <div style={{fontSize:11, color:T.textFaint}}>{b.Phone}</div>}
                        </button>
                      ))}
                    </div>
                  )}
                  {buyerSearch.trim() && filteredBuyers.length === 0 && (
                    <div style={{fontSize:11, color:T.textFaint, padding:"6px 0"}}>
                      No buyers found — add them in the Buyers List tab first.
                    </div>
                  )}
                </>
              )}
            </div>
            )}
          </div>

          <div style={{marginBottom:12}}>
            <label style={{display:"block",fontSize:12,fontWeight:600,color:T.textMuted,marginBottom:6}}>
              Bulk Order Discount
            </label>
            <select style={{...S.input,marginBottom:10}}
              value={singleDiscountPct}
              onChange={e=>setSingleDiscountPct(parseInt(e.target.value,10))}>
              <option value={0}>No discount</option>
              {Array.from({length:19},(_, i)=>(i+1)*5).map(pct=>(
                <option key={pct} value={pct}>{pct}% off</option>
              ))}
            </select>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {PAYMENT_TYPES.map(pt=>(
                <button key={pt}
                  style={{flex:1, padding:"9px 4px", fontSize:14, fontWeight:600, borderRadius:10,
                    border:`1px solid ${singlePayment===pt?"#3C66EE":T.border}`,
                    background: singlePayment===pt ? "#3C66EE" : "transparent",
                    color: singlePayment===pt ? "#fff" : T.textMuted, cursor:"pointer", minWidth:56}}
                  onClick={()=>setSinglePayment(pt)}>{pt}</button>
              ))}
            </div>
          </div>

          <div style={{display:"flex",gap:10}}>
            <button style={{...S.btn("ghost"),flex:1}} onClick={()=>setSelected(null)}>← Back</button>
            <button style={{...S.btn("primary"),flex:2,padding:"14px",fontSize:16}}
              onClick={confirmSingle} disabled={saving||!price}>
              {saving?"Saving…":`Confirm Sale ${price?`$${(parseFloat(price)*(1-singleDiscountPct/100)).toFixed(2)}`:""}`}
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{display:"flex",gap:8,marginBottom:10}}>
            <input style={{...S.input,fontSize:16,padding:"12px 14px",flex:1}}
              value={search} onChange={e=>setSearch(e.target.value)}
              placeholder={showCards.length>0?`Search ${showCards.length} cards...`:"No show cards loaded"} autoFocus />
            <button style={{padding:"12px 10px",borderRadius:8,border:`1px solid ${T.border}`,
              background:T.surface2,fontSize:12,cursor:"pointer",flexShrink:0,
              fontWeight:600,color:T.textMuted,minWidth:52}}
              onClick={()=>setSort(s=>s==="asc"?"desc":"asc")}>
              A{sort==="asc"?"↑":"↓"}
            </button>
            <button style={{padding:"12px 14px",borderRadius:8,border:`1px solid ${T.border}`,
              background:T.surface2,fontSize:20,cursor:"pointer",flexShrink:0}}
              onClick={()=>setScanning(true)}>📷</button>
          </div>

          {scanning && (
            <QRScanner
              onScan={(raw)=>{
                setScanning(false);
                if (!raw) return;
                let code = raw;
                try {
                  const url = new URL(raw);
                  code = url.searchParams.get("s") || url.searchParams.get("sell") || raw;
                } catch (_) {  }
                const cleanId = code.replace(/^cv_/, "");
                const card =
                  showCards.find(c => c.QRCode === code) ||
                  showCards.find(c => c.ID === cleanId) ||
                  allInventory.find(c => c.QRCode === code) ||
                  allInventory.find(c => c.ID === cleanId);
                if (card) selectCard(card);
                else setSearch(cleanId);
              }}
              onFail={()=>setScanning(false)}
              onClose={()=>setScanning(false)}
            />
          )}

          {showCards.length===0 && (
            <div style={{textAlign:"center",padding:"40px 0",color:T.textFaint}}>
              <div style={{fontSize:32,marginBottom:8}}>📭</div>
              <div style={{fontSize:14}}>No cards assigned to this show</div>
            </div>
          )}

          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:8}}>
            {results.slice(0,visibleCount).map(item=>{
              const inCart = mode==="multi" && cart.find(x=>x.ID===item.ID);
              return (
                <div key={item.ID}
                  style={{cursor:"pointer",borderRadius:8,overflow:"hidden",position:"relative",
                    border:`2px solid ${inCart?"#4ade80":T.border}`,
                    background:inCart?"rgba(74,222,128,0.08)":T.surface2,
                    display:"flex",flexDirection:"column"}}
                  onClick={()=>selectCard(item)}>
                  <button
                    onClick={e=>{
                      e.stopPropagation();
                      const cardUrl = `https://app.vendorstax.com?s=${item.QRCode||item.ID}`;
                      navigator.clipboard.writeText(cardUrl).then(()=>{
                        const el = e.currentTarget;
                        el.textContent = "✓";
                        setTimeout(()=>{ if(el) el.textContent = "🏷"; }, 1200);
                      });
                    }}
                    style={{
                      position:"absolute", top:4, right:4, zIndex:2,
                      width:24, height:24, borderRadius:6,
                      background:"rgba(0,0,0,0.55)", color:"#fff",
                      border:"none", cursor:"pointer",
                      fontSize:12, display:"flex", alignItems:"center", justifyContent:"center",
                      lineHeight:1,
                    }}
                    title="Copy label URL">
                    🏷
                  </button>
                  {item.IsConsignment && (
                    <div style={{
                      position:"absolute", top:4, left:4, zIndex:2,
                      background:"rgba(245,158,11,0.92)", color:"#1a1200",
                      fontSize:9, fontWeight:800, letterSpacing:"0.04em",
                      padding:"2px 6px", borderRadius:5,
                    }}>
                      💎 CONSIGN
                    </div>
                  )}
                  {item.ImageURL ? (
                    <img src={item.ImageURL} alt={item.CardName} loading="lazy"
                      style={{width:"100%",aspectRatio:"3/4",objectFit:"cover"}} />
                  ) : (
                    <div style={{width:"100%",aspectRatio:"3/4",background:T.surface,
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:28,color:T.textFaint}}>⬡</div>
                  )}
                  <div style={{padding:"5px 6px"}}>
                    <div style={{fontSize:11,fontWeight:700,lineHeight:1.2,
                      overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {item.CardName}
                    </div>
                    <div style={{fontSize:16,fontWeight:800,color:P.green,marginTop:2}}>
                      {fmtT(item.ListPrice)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {results.length > visibleCount && (
            <button style={{...S.btn("ghost"),width:"100%",padding:"10px",fontSize:13}}
              onClick={()=>setVisibleCount(c=>c+40)}>
              Load more ({results.length-visibleCount} remaining)
            </button>
          )}
        </>
      )}

      {showPriceModal && selected && (
        <div style={{position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:400,
          display:"flex", alignItems:"center", justifyContent:"center", padding:20}}
          onClick={e=>{ if (e.target===e.currentTarget && !savingPrice) setShowPriceModal(false); }}>
          <div style={{...S.card, maxWidth:360, width:"100%"}}>
            <div style={{fontSize:16, fontWeight:700, marginBottom:8}}>Save new price?</div>
            <div style={{fontSize:13, color:T.textMuted, marginBottom:18, lineHeight:1.6}}>
              {selected.CardName}: <strong style={{color:P.blue}}>${Math.round(parseFloat(selected.ListPrice)||0)}</strong>
              {" → "}
              <strong style={{color:P.green}}>${Math.round(parseFloat(price)||0)}</strong>
            </div>
            <button
              onClick={saveBothPrices} disabled={savingPrice}
              style={{width:"100%", padding:"13px", marginBottom:10, borderRadius:10,
                border:"none", background:"#3C66EE", color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer"}}>
              {savingPrice ? "Saving…" : "Update Both Show and Online Price"}
            </button>
            <button
              onClick={saveShowPriceOnly} disabled={savingPrice}
              style={{width:"100%", padding:"13px", marginBottom:10, borderRadius:10,
                border:`1px solid ${T.border}`, background:"transparent", color:T.textMuted,
                fontSize:14, fontWeight:700, cursor:"pointer"}}>
              {savingPrice ? "Saving…" : "Update Show Price Only"}
            </button>
            <button
              onClick={()=>setShowPriceModal(false)} disabled={savingPrice}
              style={{width:"100%", padding:"11px", borderRadius:10, border:"none",
                background:"transparent", color:T.textFaint, fontSize:13, cursor:"pointer"}}>
              Cancel
            </button>
            <div style={{fontSize:11, color:T.textFaint, marginTop:12, lineHeight:1.5, textAlign:"center"}}>
              "Show Only" keeps your online shop price exactly as it is now — only this show's
              price changes. "Both" updates your online shop to match.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── TRADE TAB ────────────────────────────────────────────────────────────────
function TradeTab({ show, showCards, allInventory, today, setNames, vendorId, onUpdateItem, onAddInventoryItem, onRemoveShowItem, onUnlinkShowItem, onAddShowItem, onAddTrade, dayMode, S, T }) {
  const { toast } = useNotify();
  const [rate, setRate]           = useState(80);
  const [customRate, setCustomRate] = useState("");
  const [useCustomRate, setUseCustomRate] = useState(false);
  const [cardsOut, setCardsOut]   = useState([]);
  const [cardsIn, setCardsIn]     = useState([]);
  const [historyTid, setHistoryTid] = useState(null);
  const [outSearch, setOutSearch] = useState("");
  const [inSearch, setInSearch]   = useState("");
  const [saving, setSaving]       = useState(false);
  const [done, setDone]           = useState(null);
  const [scanningOut, setScanningOut] = useState(false);
  const [scanningIn, setScanningIn]   = useState(false);
  const [inSearchMode, setInSearchMode] = useState("inventory");
  const [inGameType, setInGameType] = useState("Pokemon");
  const [activeTab, setActiveTab] = useState("card");
  const [apiResults, setApiResults]   = useState([]);
  const [apiSearching, setApiSearching] = useState(false);
  const [apiSearchPage, setApiSearchPage] = useState(1);
  const [apiHasMore, setApiHasMore] = useState(false);
  const [apiLoadingMore, setApiLoadingMore] = useState(false);
  const [inLang, setInLang]       = useState("English");
  const [tradePhoto, setTradePhoto] = useState("");
  const tradePhotoRef = useRef(null);
  const condSeq = useRef({});

  const D = dayMode ? {
    bg:        "#f5f5f0",
    card:      "#ffffff",
    border:    "#cccccc",
    text:      "#111111",
    textMuted: "#444444",
    textFaint: "#666666",
    input:     { background:"#ffffff", border:"1px solid #bbb", color:"#111" },
    greenBg:   "#f0fdf4",
    greenBorder:"#16a34a",
    greenText: "#15803d",
    greenLabel:"#166534",
    blueTxt:   "#1d4ed8",
    redTxt:    "#b91c1c",
    tabColor:  "#111",
    tabActive: { background:"#ffffff", color:"#111" },
    tabInactive:{ color:"#333", fontWeight:600 },
  } : {
    bg:        T.surface,
    card:      T.surface2,
    border:    T.border,
    text:      T.text,
    textMuted: T.textMuted,
    textFaint: T.textFaint,
    input:     {},
    greenBg:   "#0f2e1a",
    greenBorder:"#166534",
    greenText: "#4ade80",
    greenLabel:"#86efac",
    blueTxt:   "#60a5fa",
    redTxt:    "#f87171",
    tabColor:  T.textMuted,
    tabActive: { background:"#2a2a3e", color:"#fff" },
    tabInactive:{ color:"#777" },
  };

  const effectiveRate = useCustomRate ? (parseFloat(customRate)||0) : rate;

  // ── Out search — show cards ONLY (cards added to this show) ──────────────
  const outResults = useMemo(()=>{
    if (!outSearch.trim()) return [];
    const q = outSearch.toLowerCase();
    return showCards.filter(i=>
      i.CardName?.toLowerCase().includes(q) ||
      i.Set?.toLowerCase().includes(q)
    );
  },[outSearch, showCards]);

  // ── In search — inventory lookup ────────────────────────────────────────────
  const inResults = useMemo(()=>{
    if (!inSearch.trim()) return [];
    const q = inSearch.toLowerCase();
    return allInventory.filter(i=>
      i.Status!=="Sold"&&i.Status!=="Deleted"&&
      (i.CardName?.toLowerCase().includes(q)||i.Set?.toLowerCase().includes(q))&&
      !cardsOut.find(c=>c.ID===i.ID)
    );
  },[inSearch,allInventory,cardsOut]);

  useEffect(() => {
    if (inSearchMode !== "api" || !inSearch.trim()) { setApiResults([]); setApiHasMore(false); return; }
    let cancelled = false;
    setApiSearching(true);
    setApiSearchPage(1);
    const timer = setTimeout(async () => {
      if (activeTab === "sealed") {
        const { results, error } = await apitcgSearchSealed(inGameType, inSearch.trim(), 1);
        if (!cancelled) {
          setApiResults(results || []);
          setApiHasMore(false);
          setApiSearching(false);
        }
        return;
      }
      const numberMatch = inSearch.trim().match(/^(.+)\s+([A-Z]{0,4}\d+(?:\/[A-Z]{0,4}\d+)?)$/i);
      const name = numberMatch ? numberMatch[1].trim() : inSearch.trim();
      const number = numberMatch ? numberMatch[2] : undefined;
      const { results, pagination } = await unifiedCardSearch(inGameType, name, number, 1);
      if (!cancelled) {
        setApiResults(results || []);
        setApiHasMore(!!pagination && pagination.page < pagination.total_pages);
        setApiSearching(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [inSearch, inSearchMode, activeTab]);

  const loadMoreTradeApiResults = async () => {
    const numberMatch = inSearch.trim().match(/^(.+)\s+([A-Z]{0,4}\d+(?:\/[A-Z]{0,4}\d+)?)$/i);
    const name = numberMatch ? numberMatch[1].trim() : inSearch.trim();
    const number = numberMatch ? numberMatch[2] : undefined;
    const nextPage = apiSearchPage + 1;
    setApiLoadingMore(true);
    const { results, pagination } = await unifiedCardSearch(inGameType, name, number, nextPage);
    setApiResults(prev => [...prev, ...(results || [])]);
    setApiHasMore(!!pagination && pagination.page < pagination.total_pages);
    setApiSearchPage(nextPage);
    setApiLoadingMore(false);
  };

  const addApiCardIn = async (result) => {
    const tid = "ti_"+Date.now();
    const isSealed = activeTab === "sealed";
    setCardsIn(p=>[...p,{
      CardName: result.cardName || "",
      Set:      result.setName  || "",
      Number:   isSealed ? "" : (result.cardNumber || ""),
      Rarity:   isSealed ? "" : (result.rarity   || "Rare"),
      Condition: isSealed ? "New" : "Near Mint",
      Language: inLang,
      Type:     isSealed ? "Sealed Product" : "Single Card",
      GameType: inGameType,
      ImageURL: result.imageUrl || "",
      TcggoId:  result.tcggoId || null,
      _tid:     tid,
      _mv:      "",
      _credit:  0,
      _isNew:   true,
      _apiMatched: true,
    }]);
    setInSearch(""); setApiResults([]);
    const priced = isSealed
      ? await apitcgLookupSealed(inGameType, result.cardName, result.tcggoId)
      : await unifiedCardLookup(inGameType, result.cardName, result.cardNumber, result.setName, result.tcggoId);
    if (priced?.center != null) {
      setCardsIn(p => p.map(x => x._tid === tid
        ? { ...x, _mv: String(Math.round(priced.center)), _credit: Math.round(priced.center * (x._cardRate??rate) / 100), _priceInfo: priced }
        : x));
    }
  };

  // ── Language-aware set list for cards coming in ────────────────────────────
  const inSetList = useMemo(()=>{
    let sets;
    if (inLang==="Chinese")  sets = [...CONFIG.CHINESE_SETS];
    else if (inLang==="Japanese") sets = CONFIG.JAPANESE_SETS ? [...CONFIG.JAPANESE_SETS] : [];
    else sets = CONFIG.POKEMON_SETS.map(s=>s.name);
    return sets.sort((a,b)=>a.toLowerCase().localeCompare(b.toLowerCase()));
  },[inLang]);

  const addCardOut = (item, fromScan = false)=>{
    const already = cardsOut.find(c=>c.ID===item.ID);
    if (!already) {
      setCardsOut(p=>[...p,{...item,_outPrice:String(Math.round(item.ListPrice||0))}]);
      if (fromScan) toast(`Added: ${item.CardName}`, "success");
    } else if (fromScan) {
      toast(`${item.CardName} is already in this list`, "alert");
    }
    setOutSearch("");
  };

  const addCardIn = (item)=>{
    const mv = item.MarketValueAtAcquisition||item.ListPrice||0;
    setCardsIn(p=>[...p,{...item,_tid:"ti_"+Date.now()+Math.random(),
      _mv:mv?String(Math.round(mv)):"",_credit:mv?Math.round(mv*effectiveRate/100):0,_isNew:false}]);
    setInSearch("");
  };

  const addNewCardIn = (name)=>{
    if(!name.trim()) return;
    const isSealed = activeTab === "sealed";
    setCardsIn(p=>[...p,{CardName:name,Set:"",Number:"",Rarity:isSealed?"":"Rare",Condition:isSealed?"New":"Near Mint",
      Language:inLang,Type:isSealed?"Sealed Product":"Single Card",GameType:inGameType,LorcanaInkColor:"",LorcanaFoilType:"Normal",ImageURL:"",
      _tid:"ti_"+Date.now(),_mv:"",_credit:0,_isNew:true}]);
    setInSearch("");
  };

  const updateMV = (tid,mv)=>setCardsIn(p=>p.map(c=>c._tid!==tid?c:
    {...c,_mv:mv,_credit:mv?Math.round(parseFloat(mv)*effectiveRate/100):0}));

  const changeInCondition = async (tid, condition) => {
    const card = cardsIn.find(x => x._tid === tid);
    setCardsIn(p => p.map(x => x._tid === tid ? { ...x, Condition: condition } : x));
    if (!card || card.Type === "Sealed Product") return;
    const game = card.GameType || inGameType;
    if (game !== "Pokemon") return;
    const seq = (condSeq.current[tid] || 0) + 1;
    condSeq.current[tid] = seq;
    const cached = card._priceInfo?._priceData;
    let priced = null;
    if (cached) {
      const base = computeConditionPrice(cached, condition);
      if (base) priced = { ...base, tcggoId: card._priceInfo.tcggoId, _priceData: cached };
    } else {
      priced = await unifiedCardLookup(game, card.CardName, card.Number, card.Set, card.TcggoId, null, null, condition);
    }
    if (condSeq.current[tid] !== seq || priced?.center == null) return;
    setCardsIn(p => p.map(x => x._tid === tid
      ? { ...x, _mv: String(Math.round(priced.center)), _credit: Math.round(priced.center * (x._cardRate ?? effectiveRate) / 100), _priceInfo: priced }
      : x));
  };

  const changeRate = r=>{ setRate(r); setUseCustomRate(false); setCustomRate("");
    setCardsIn(p=>p.map(c=>({...c,_credit:c._mv?Math.round(parseFloat(c._mv)*r/100):0}))); };
  const applyCustom = r=>{ const pct=parseFloat(r)||0; setUseCustomRate(true);
    setCardsIn(p=>p.map(c=>({...c,_credit:c._mv?Math.round(parseFloat(c._mv)*pct/100):0}))); };

  const totalOut    = cardsOut.reduce((s,c)=>s+Math.round(parseFloat(c._outPrice)||c.ListPrice||0),0);
  const totalCredit = cardsIn.reduce((s,c)=>s+(c._credit||0),0);
  const balance     = totalCredit-totalOut;

  const confirm = async(addToShow)=>{
    if(!cardsOut.length||!cardsIn.length) return;
    setSaving(true);
    for (const card of cardsOut) {
      const fullValue = parseFloat(card._outPrice) || parseFloat(card.ListPrice) || 0;
      const tradeGain = Math.max(0, fullValue - (card.AcquisitionCost || 0));
      await onUpdateItem(card.ID,{Status:"Sold",SoldDate:today,SoldAt:new Date().toISOString(),SoldChannel:"Trade",
        SoldPrice:fullValue, TradeGain: tradeGain || null,
        ShowID:show.ID,ShowName:show.Name});
      removeDealBoardListingIfListed(card.ID);
      if (onUnlinkShowItem) await onUnlinkShowItem(show.ID, card.ID);
      else if (onRemoveShowItem) await onRemoveShowItem(show.ID, card.ID).catch(()=>{});
    }
    for (const card of cardsIn) {
      const mv=parseFloat(card._mv)||0; const ac=card._credit||0;
      const newItem = await onAddInventoryItem({
        Type:card.Type||"Single Card",CardName:card.CardName,Set:card.Set||"",
        Number:card.Number||"",Rarity:card.Rarity||"Rare",Language:card.Language||"English",
        Condition:card.Condition||"Near Mint",Grade:"",Quantity:1,Source:"Customer Trade",
        GameType:card.GameType||"Pokemon",
        LorcanaInkColor:card.GameType==="Lorcana"?(card.LorcanaInkColor||""):"",
        LorcanaFoilType:card.GameType==="Lorcana"?(card.LorcanaFoilType||"Normal"):"Normal",
        AcquisitionCost:ac,ListPrice:"",MinSellPrice:ac>0?minSell(ac):0,
        MarketValueAtAcquisition:mv,AcquisitionDate:today,Status:"In Stock",
        ShowID:show.ID,ShowName:show.Name,AcquiredShowID:show.ID,LotID:"",LotName:"",LotItemNumber:"",
        ImageURL:card.ImageURL||"",Notes:`Trade-in at ${show.Name} · ${effectiveRate}% credit`,
      });
      if (addToShow && onAddShowItem && newItem?.ID) {
        await onAddShowItem(show.ID, newItem.ID);
      }
    }
    await onAddTrade({Date:today,CardsOut:cardsOut.map(c=>c.CardName).join(", "),
      CardsIn:cardsIn.map(c=>c.CardName).join(", "),TotalCreditValue:totalCredit,
      CreditRate:effectiveRate/100,Type:"Trade Credit",Notes:show.Name,
      CashReceived:balance !== 0 ? balance : null});
    if (tradePhoto && vendorId) {
      try {
        await addTradePhoto(vendorId, show.ID, tradePhoto,
          cardsOut.map(c=>c.CardName).join(", "), cardsIn.map(c=>c.CardName).join(", "));
      } catch(e) {
        console.warn("Could not save trade photo:", e.message);
      }
    }
    setDone({out:cardsOut.length,in:cardsIn.length,credit:totalCredit});
    setCardsOut([]); setCardsIn([]); setSaving(false); setTradePhoto("");
  };

  if (done) return (
    <div style={{padding:16,textAlign:"center",paddingTop:60}}>
      <div style={{fontSize:40,marginBottom:12}}>🔄</div>
      <div style={{fontSize:18,fontWeight:700,marginBottom:8}}>Trade Complete</div>
      <div style={{fontSize:14,color:T.textMuted,marginBottom:4}}>
        {done.out} card{done.out>1?"s":""} out · {done.in} card{done.in>1?"s":""} in
      </div>
      <div style={{fontSize:16,color:"#4ade80",fontWeight:600,marginBottom:32}}>${done.credit} credit applied</div>
      <button style={{...S.btn("primary"),padding:"12px 32px"}} onClick={()=>setDone(null)}>New Trade</button>
    </div>
  );

  return (
    <div style={{padding:16, maxWidth:"100%", boxSizing:"border-box",
      background: dayMode ? "#f5f5f0" : "transparent",
      color: T.text,
      minHeight:"100%"}}>

      {/* ── Credit rate dropdown ──────────────────────────────────────────── */}
      <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:14}}>
        <label style={{...S.label, marginBottom:0, flexShrink:0, fontSize:14,
          color: dayMode ? "#333" : T.textMuted, fontWeight: dayMode ? 600 : 500}}>
          Credit
        </label>
        <select
          style={{flex:1, background: dayMode?"#fff":"#252535",
            border: `0.5px solid ${dayMode?"#bbb":"#444"}`,
            borderRadius:8, color: dayMode?"#111":"#fff",
            fontSize:14, padding:"10px 12px", WebkitAppearance:"none"}}
          value={useCustomRate ? "custom" : String(rate)}
          onChange={e => {
            if (e.target.value === "custom") { setUseCustomRate(true); }
            else { changeRate(parseInt(e.target.value)); }
          }}>
          <option value="100">100% — straight trade</option>
          <option value="90">90% of market</option>
          <option value="85">85% of market</option>
          <option value="80">80% of market</option>
          <option value="75">75% of market</option>
          <option value="70">70% of market</option>
          <option value="60">60% of market</option>
          <option value="custom">Custom %</option>
        </select>
        {useCustomRate && (
          <input
            style={{width:72, background: dayMode?"#fff":"#252535",
              border:"1.5px solid #3C66EE", borderRadius:8,
              color: dayMode?"#111":"#fff", fontSize:14,
              padding:"10px 8px", textAlign:"center"}}
            type="number" min="0" max="100" placeholder="%"
            value={customRate}
            onChange={e=>{ setCustomRate(e.target.value); applyCustom(e.target.value); }}
          />
        )}
      </div>

      <div style={{height:"0.5px", background: dayMode?"#ddd":T.border, marginBottom:14}}/>

      {/* ── Cards going out ──────────────────────────────────────────────── */}
      <div style={{...S.h2, color: dayMode?"#166534":"#4ade80", fontSize:15}}>Your cards going out</div>

      <div style={{display:"flex",gap:8,marginBottom:8}}>
        <input style={{...S.input,flex:1}} value={outSearch}
          onChange={e=>setOutSearch(e.target.value)} placeholder="Search by name or set…" />
        <button style={{padding:"10px 12px",borderRadius:8,border:`1px solid ${T.border}`,
          background:T.surface2,fontSize:18,cursor:"pointer",flexShrink:0}}
          onClick={()=>setScanningOut(true)} title="Scan QR code">📷</button>
      </div>

      {scanningOut && (
        <QRScanner
          onScan={(raw)=>{
            setScanningOut(false);
            if (!raw) return;
            let code = raw;
            try { const u=new URL(raw); code=u.searchParams.get("s")||u.searchParams.get("sell")||raw; } catch(_){}
            const cleanId = code.replace(/^cv_/,"");
            const card =
              showCards.find(c=>c.QRCode===code||c.ID===cleanId) ||
              allInventory.find(c=>c.QRCode===code||c.ID===cleanId);
            if (card) addCardOut(card);
          }}
          onClose={()=>setScanningOut(false)}
          S={S} T={T}
        />
      )}

      {outResults.length > 0 && (
        <div style={{
          display:"grid",
          gridTemplateColumns:"repeat(auto-fill,minmax(min(100%,120px),1fr))",
          gap:8, marginBottom:8
        }}>
          {outResults.map(item=>{
            const selected = !!cardsOut.find(c=>c.ID===item.ID);
            return (
              <div key={item.ID}
                style={{cursor:"pointer",borderRadius:8,overflow:"hidden",
                  border:`2px solid ${selected?"#4ade80":T.border}`,
                  background:selected?"rgba(74,222,128,0.08)":T.surface2,
                  display:"flex",flexDirection:"column"}}
                onClick={()=>addCardOut(item)}>
                {item.ImageURL ? (
                  <img src={item.ImageURL} alt={item.CardName} loading="lazy"
                    style={{width:"100%",aspectRatio:"3/4",objectFit:"cover"}} />
                ) : (
                  <div style={{width:"100%",aspectRatio:"3/4",background:T.surface,
                    display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:24,color:T.textFaint}}>⬡</div>
                )}
                <div style={{padding:"5px 6px"}}>
                  <div style={{fontSize:15,fontWeight:700,lineHeight:1.2,
                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {item.CardName}
                  </div>
                  <div style={{fontSize:13,color:T.textMuted,marginTop:1}}>
                    {fmtT(item.ListPrice)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {cardsOut.length > 0 && (
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:4}}>
          {cardsOut.map((c,idx)=>{
            const dupNames = cardsOut.filter(x=>x.CardName===c.CardName);
            const showQR = dupNames.length > 1;
            return (
              <div key={c.ID} style={{
                background: dayMode ? "#fff" : "rgba(74,222,128,0.06)",
                border: `0.5px solid ${dayMode?"#16a34a":"rgba(74,222,128,0.4)"}`,
                borderRadius:12, padding:10,
                display:"flex", gap:12, alignItems:"center"}}>
                {c.ImageURL ? (
                  <img src={c.ImageURL} alt="" style={{width:60,height:84,objectFit:"cover",borderRadius:6,flexShrink:0}}/>
                ) : (
                  <div style={{width:60,height:84,borderRadius:6,flexShrink:0,
                    background: dayMode?"#e4e4dc":"#2a2a40",
                    display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:20,color: dayMode?"#bbb":"#444"}}>⬡</div>
                )}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:15,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",
                    whiteSpace:"nowrap", color: dayMode?"#111":T.text, marginBottom:3}}>
                    {c.CardName}
                  </div>
                  {showQR && (
                    <div style={{fontSize:12,color:"#fbbf24",fontFamily:"'DM Mono',monospace",marginBottom:4}}>
                      QR: {c.QRCode||c.ID?.slice(0,8)}
                    </div>
                  )}
                  {!showQR && <div style={{fontSize:14,color: dayMode?"#555":T.textFaint,marginBottom:6}}>{c.Set}</div>}
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                    <div style={{fontSize:13,color: dayMode?"#666":T.textMuted}}>
                      Cost: <span style={{fontWeight:600,color: dayMode?"#333":T.textFaint}}>{fmtT(c.AcquisitionCost||0)}</span>
                    </div>
                    <input
                      style={{background: dayMode?"#f0fdf4":"#1a2a1a",
                        border: `1.5px solid ${dayMode?"#16a34a":"#22c55e"}`,
                        borderRadius:8, color: dayMode?"#15803d":"#4ade80",
                        fontSize:17, fontWeight:700, padding:"6px 8px",
                        width:88, textAlign:"center", flexShrink:0,
                        fontFamily:"'DM Mono',monospace"}}
                      type="number" min="0" step="0.01"
                      value={c._outPrice||""}
                      onChange={e=>setCardsOut(p=>p.map(x=>x.ID===c.ID?{...x,_outPrice:e.target.value}:x))} />
                  </div>
                </div>
                <span style={{cursor:"pointer",fontSize:20,color: dayMode?"#555":T.textMuted,flexShrink:0,alignSelf:"flex-start"}}
                  onClick={()=>setCardsOut(p=>p.filter(x=>x.ID!==c.ID))}>×</span>
              </div>
            );
          })}
        </div>
      )}

      <div style={{height:"0.5px", background: dayMode?"#ddd":T.border, margin:"12px 0"}}/>

      {/* ── Cards coming in ──────────────────────────────────────────────── */}
      <div style={{...S.h2, color: dayMode?"#2563eb":"#60a5fa", fontSize:15}}>Cards coming in</div>

      {scanningIn && (
        <CardScanner
          S={S} T={T}
          mode="buy"
          onClose={() => setScanningIn(false)}
          onSelect={(r) => {
            setScanningIn(false);
            setCardsIn(p=>[...p,{
              CardName: r.cardName || "",
              Set:      r.setName  || "",
              Number:   r.cardNumber || "",
              Rarity:   r.rarity   || "Rare",
              Condition:"Near Mint",
              Language: inLang,
              Type:     "Single Card",
              ImageURL: r.imageUrl || "",
              TcggoId:  r.tcggoId || null,
              _tid:     "ti_"+Date.now(),
              _mv:      r.price ? String(Math.round(r.price)) : "",
              _credit:  r.price ? Math.round(r.price * rate / 100) : 0,
              _isNew:   true,
              _apiMatched: true,
            }]);
          }}
        />
      )}

      <select style={{...S.select, marginBottom:8}}
        value={inGameType}
        onChange={e=>{
          setInGameType(e.target.value);
          setInSearch(""); setApiResults([]);
        }}>
        {["Pokemon","Lorcana","Magic","One Piece","Yu-Gi-Oh"].map(g=><option key={g} value={g}>{g}</option>)}
      </select>

      <div style={{display:"flex", gap:6, marginBottom:8}}>
        {[["card","Card"],["sealed","Sealed"]].map(([tab,label])=>(
          <button key={tab}
            style={{flex:1, textAlign:"center", padding:"7px 8px", borderRadius:8, cursor:"pointer",
              border:`1px solid ${activeTab===tab ? "#3C66EE" : (dayMode?"#ccc":T.border)}`,
              background: activeTab===tab ? "rgba(60,102,238,0.12)" : "transparent",
              color: activeTab===tab ? D.blueTxt : (dayMode?"#666":T.textMuted),
              fontFamily:"'Sora',sans-serif", fontSize:12, fontWeight:600}}
            onClick={()=>{
              setActiveTab(tab);
              setInSearch(""); setApiResults([]);
            }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{display:"flex", gap:6, marginBottom:8}}>
        {[["inventory","✍️ Add Manually"], ["api","🔍 Search Market Price"]].map(([m,label])=>(
          <button key={m}
            onClick={()=>{ setInSearchMode(m); setInSearch(""); setApiResults([]); }}
            style={{flex:1, padding:"7px 8px", borderRadius:8, cursor:"pointer",
              fontFamily:"'Sora',sans-serif", fontSize:12, fontWeight:600,
              border:`1px solid ${inSearchMode===m ? "#3C66EE" : T.border}`,
              background: inSearchMode===m ? "rgba(60,102,238,0.12)" : "transparent",
              color: inSearchMode===m ? D.blueTxt : T.textMuted}}>
            {label}
          </button>
        ))}
      </div>

      <div style={{display:"flex", gap:8, alignItems:"center", marginBottom:8}}>
        <input style={{...S.input, flex:1}} value={inSearch}
          onChange={e=>setInSearch(e.target.value)}
          placeholder={inSearchMode==="api" ? "Search by card name…" : "Search inventory or type card name…"}
          onKeyDown={e=>{ if(e.key==="Enter"&&inSearch.trim()&&inSearchMode==="inventory"){addNewCardIn(inSearch);setInSearch("");}}} />
        <button
          onClick={() => setScanningIn(true)}
          title="Scan card with camera"
          style={{
            background:"rgba(60,102,238,0.12)",
            border:"0.5px solid rgba(60,102,238,0.4)",
            borderRadius:8, color:"#88bdf2",
            fontSize:20, padding:"8px 12px",
            cursor:"pointer", flexShrink:0, lineHeight:1,
          }}>
          📷
        </button>
      </div>

      {inSearchMode === "api" ? (
        <>
          {apiSearching && (
            <div style={{fontSize:12, color:T.textFaint, marginBottom:8}}>Searching…</div>
          )}
          {apiResults.map((item,i)=>(
            <div key={item.tcggoId||i}
              style={{background: dayMode?"#fff":"#1e1e30",
                border:`0.5px solid ${dayMode?"#ccc":T.border}`,
                borderRadius:10, padding:8, marginBottom:6,
                cursor:"pointer", display:"flex", gap:10, alignItems:"center"}}
              onClick={()=>addApiCardIn(item)}>
              {item.imageUrl ? (
                <img src={item.imageUrl} alt="" style={{width:60,height:84,objectFit:"cover",borderRadius:6,flexShrink:0}}/>
              ) : (
                <div style={{width:60,height:84,borderRadius:6,flexShrink:0,
                  background: dayMode?"#e4e4dc":"#2a2a40",
                  display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:16, color: dayMode?"#bbb":"#444"}}>⬡</div>
              )}
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:14,fontWeight:600,color: dayMode?"#111":T.text}}>{item.cardName}</div>
                <div style={{fontSize:14,color: dayMode?"#555":T.textMuted}}>
                  {item.setName}{item.cardNumber ? ` · #${item.cardNumber}` : ""}
                </div>
              </div>
              <span style={{fontSize:14,fontWeight:600,color: dayMode?"#15803d":"#4ade80",flexShrink:0}}>+ Add</span>
            </div>
          ))}
          {apiHasMore && (
            <div style={{textAlign:"center", marginTop:4, marginBottom:8}}>
              <span style={{fontSize:13, fontWeight:600, color:D.blueTxt, cursor:"pointer"}}
                onClick={loadMoreTradeApiResults}>
                {apiLoadingMore ? "Loading…" : "More results"}
              </span>
            </div>
          )}
        </>
      ) : (
      <>
      {inResults.map(item=>(
        <div key={item.ID}
          style={{background: dayMode?"#fff":"#1e1e30",
            border:`0.5px solid ${dayMode?"#ccc":T.border}`,
            borderRadius:10, padding:8, marginBottom:6,
            cursor:"pointer", display:"flex", gap:10, alignItems:"center"}}
          onClick={()=>addCardIn(item)}>
          {item.ImageURL ? (
            <img src={item.ImageURL} alt="" style={{width:60,height:84,objectFit:"cover",borderRadius:6,flexShrink:0}}/>
          ) : (
            <div style={{width:60,height:84,borderRadius:6,flexShrink:0,
              background: dayMode?"#e4e4dc":"#2a2a40",
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:16, color: dayMode?"#bbb":"#444"}}>⬡</div>
          )}
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:14,fontWeight:600,color: dayMode?"#111":T.text}}>{item.CardName}</div>
            <div style={{fontSize:14,color: dayMode?"#555":T.textMuted}}>{item.Set} · {item.Condition}</div>
            <div style={{fontSize:15,fontWeight:600,color: dayMode?"#1d4ed8":"#60a5fa",marginTop:2}}>
              {item.ListPrice ? `$${Math.round(item.ListPrice)}` : "—"}
            </div>
          </div>
          <span style={{fontSize:14,fontWeight:600,color: dayMode?"#15803d":"#4ade80",flexShrink:0}}>+ Add</span>
        </div>
      ))}
      {inSearchMode==="inventory" && inSearch.trim() && (
        <div style={{...S.card,cursor:"pointer",padding:"8px 12px",marginBottom:6,
          borderColor:"rgba(60,102,238,0.4)",background:T.blueBg}}
          onClick={()=>{addNewCardIn(inSearch);setInSearch("");}}>
          <div style={{fontSize:15,color:D.blueTxt,fontWeight:600}}>+ Add "{inSearch}" as new card</div>
          <div style={{fontSize:13,color:T.textMuted}}>
            {inResults.length > 0 ? "Add a new copy (not from your inventory)" : "Will be added to inventory after trade"}
          </div>
        </div>
      )}
      </>
      )}

      {cardsIn.map(c=>(
        <div key={c._tid} style={{
          background: dayMode ? "#eff6ff" : "rgba(96,165,250,0.08)",
          border: `0.5px solid ${dayMode?"#2563eb":"rgba(96,165,250,0.4)"}`,
          borderRadius:12, padding:10, marginBottom:8}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div style={{flex:1}}>
              <div style={{fontSize:15,fontWeight:500}}>{c.CardName}</div>
              {(!c._isNew || c._apiMatched) && <div style={{fontSize:13,color:T.textMuted}}>{c.Set} · {c.Condition}</div>}
            </div>
            <span style={{cursor:"pointer",fontSize:16,color:T.textMuted}}
              onClick={()=>setCardsIn(p=>p.filter(x=>x._tid!==c._tid))}>×</span>
          </div>
          {c._apiMatched && c.Type !== "Sealed Product" && (
            <div style={{marginBottom:8}}>
              <div style={S.statLbl}>Condition</div>
              <select style={{...S.select,padding:"8px 10px",fontSize:15}}
                value={c.Condition||"Near Mint"}
                onChange={e=>changeInCondition(c._tid,e.target.value)}>
                {CONDITIONS.map(cd=><option key={cd}>{cd}</option>)}
              </select>
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:"1fr auto 1fr",gap:8,marginBottom:8}}>
            <div>
              <div style={S.statLbl}>Market Value ($)</div>
              <input style={{...S.input,padding:"8px 10px",fontSize:15}} type="number"
                value={c._mv} placeholder="0" onChange={e=>updateMV(c._tid,e.target.value)} />
              {c._priceInfo?._noConditionPricing && (
                <div style={{fontSize:11, color:T.textFaint, marginTop:4}}>
                  {c.Type==="Sealed Product" ? "Market Price" : "Market Price · Near Mint"}
                </div>
              )}
              {c._priceInfo?.period === "estimated" && !c._priceInfo._noConditionPricing && c.Type !== "Sealed Product" && (
                <div style={{fontSize:11, color:T.textMuted, marginTop:4}}>
                  No {c.Condition} sales found, estimated price
                </div>
              )}
              {(c._priceInfo?._priceData || c._priceInfo?._rawRows) && (
                <div style={{fontSize:11, fontWeight:700, color:D.blueTxt, marginTop:4, cursor:"pointer"}}
                  onClick={()=>setHistoryTid(c._tid)}>
                  View History
                </div>
              )}
            </div>
            <div>
              <div style={S.statLbl}>Rate %</div>
              <input style={{...S.input,padding:"8px 6px",fontSize:15,width:60,textAlign:"center"}}
                type="number" min={0} max={100}
                value={c._cardRate!==undefined ? c._cardRate : rate}
                onChange={e=>{
                  const r = parseFloat(e.target.value)||0;
                  setCardsIn(p=>p.map(x=>x._tid!==c._tid?x:
                    {...x,_cardRate:r,_credit:x._mv?Math.round(parseFloat(x._mv)*r/100):0}));
                }} />
            </div>
            <div>
              <div style={S.statLbl}>Credit</div>
              <div style={{background:c._credit>0?D.greenBg:T.surface2,
                border:`0.5px solid ${c._credit>0?D.greenBorder:T.border}`,
                borderRadius:8,padding:"8px 10px",fontSize:15,fontFamily:"'DM Mono',monospace",
                color:c._credit>0?D.greenText:T.textFaint,fontWeight:c._credit>0?700:400}}>
                {c._credit>0?fmtT(c._credit):"—"}
              </div>
            </div>
          </div>

          {c._isNew && !c._apiMatched && (
            <>
              <select style={{...S.select, marginBottom:6}}
                value={c.GameType}
                onChange={e=>{
                  const g = e.target.value;
                  setCardsIn(p=>p.map(x=>x._tid!==c._tid?x:
                    {...x, GameType:g, Set:"", Language: g==="Pokemon" ? (x.Language||"English") : "English"}));
                }}>
                {["Pokemon","Lorcana","Magic","One Piece","Yu-Gi-Oh"].map(g=><option key={g} value={g}>{g}</option>)}
              </select>

              {c.GameType==="Pokemon" && (
                <div style={{marginBottom:6}}>
                  <div style={S.statLbl}>Language</div>
                  <select style={{...S.select,fontSize:14}}
                    value={c.Language||"English"}
                    onChange={e=>{
                      const lang = e.target.value;
                      setCardsIn(p=>p.map(x=>x._tid===c._tid?{...x,Language:lang,Set:""}:x));
                      setInLang(lang);
                    }}>
                    <option>English</option>
                    <option>Japanese</option>
                    <option>Chinese</option>
                    <option>Korean</option>
                    <option>Other</option>
                  </select>
                </div>
              )}

              <div style={{display:"flex",gap:6,marginBottom:6}}>
                <select style={{...S.select,flex:2,fontSize:14}}
                  value={c.Set||""} onChange={e=>setCardsIn(p=>p.map(x=>x._tid===c._tid?{...x,Set:e.target.value}:x))}>
                  <option value="">Select set (optional)</option>
                  {(c.GameType==="Lorcana"
                    ? [...(CONFIG.LORCANA_SETS||[])].sort((a,b)=>a.toLowerCase().localeCompare(b.toLowerCase()))
                    : c.GameType==="Magic"
                    ? [...(CONFIG.MAGIC_SETS||[])].sort((a,b)=>a.toLowerCase().localeCompare(b.toLowerCase()))
                    : c.GameType==="One Piece"
                    ? [...(CONFIG.ONEPIECE_SETS||[])].sort((a,b)=>a.toLowerCase().localeCompare(b.toLowerCase()))
                    : c.GameType==="Yu-Gi-Oh"
                    ? [...(CONFIG.YUGIOH_SETS||[])].sort((a,b)=>a.toLowerCase().localeCompare(b.toLowerCase()))
                    : c.Language==="Japanese"
                    ? [...(CONFIG.JAPANESE_SETS||[])].sort((a,b)=>a.toLowerCase().localeCompare(b.toLowerCase()))
                    : c.Language==="Chinese"
                    ? [...(CONFIG.CHINESE_SETS||[])].sort((a,b)=>a.toLowerCase().localeCompare(b.toLowerCase()))
                    : CONFIG.POKEMON_SETS.map(s=>s.name).sort((a,b)=>a.toLowerCase().localeCompare(b.toLowerCase()))
                  ).map(s=><option key={s} value={s}>{s}</option>)}
                </select>
                {c.Type!=="Sealed Product" && (
                <input style={{...S.input,width:90,fontSize:14}} value={c.Number||""}
                  placeholder="Card #"
                  onChange={e=>setCardsIn(p=>p.map(x=>x._tid===c._tid?{...x,Number:e.target.value}:x))} />
                )}
              </div>

              {c.GameType==="Lorcana" && c.Type!=="Sealed Product" && (
                <div style={{display:"flex",gap:6,marginBottom:6}}>
                  <select style={{...S.select,flex:1,fontSize:13}}
                    value={c.LorcanaInkColor||""}
                    onChange={e=>setCardsIn(p=>p.map(x=>x._tid===c._tid?{...x,LorcanaInkColor:e.target.value}:x))}>
                    <option value="">Ink color</option>
                    {CONFIG.LORCANA_INK_COLORS.map(ink=><option key={ink}>{ink}</option>)}
                  </select>
                  <select style={{...S.select,flex:1,fontSize:13}}
                    value={c.LorcanaFoilType||"Normal"}
                    onChange={e=>setCardsIn(p=>p.map(x=>x._tid===c._tid?{...x,LorcanaFoilType:e.target.value}:x))}>
                    {CONFIG.LORCANA_FOIL_TYPES.map(f=><option key={f}>{f}</option>)}
                  </select>
                </div>
              )}

              {c.Type!=="Sealed Product" && (
              <div style={{marginBottom:6}}>
                <div style={S.statLbl}>Condition</div>
                <select style={{...S.select,fontSize:14}}
                  value={c.Condition||"Near Mint"}
                  onChange={e=>setCardsIn(p=>p.map(x=>x._tid===c._tid?{...x,Condition:e.target.value}:x))}>
                  {["Mint","Near Mint","Lightly Played","Moderately Played","Heavily Played","Damaged"].map(cd=>(
                    <option key={cd}>{cd}</option>
                  ))}
                </select>
              </div>
              )}

              <div>
                <div style={S.statLbl}>Image URL
                  <span style={{color:T.textFaint,fontWeight:400,textTransform:"none",marginLeft:4}}>(optional — paste from TCGPlayer)</span>
                </div>
                <input style={{...S.input,fontSize:13}} value={c.ImageURL||""}
                  placeholder="https://product.images.tcgplayer.com/..."
                  onChange={e=>setCardsIn(p=>p.map(x=>x._tid===c._tid?{...x,ImageURL:e.target.value}:x))} />
                {c.ImageURL && (
                  <div style={{marginTop:6}}>
                    <img src={c.ImageURL} alt="preview"
                      style={{height:80,objectFit:"contain",borderRadius:6,border:`0.5px solid ${T.border}`}}
                      onError={e=>{e.target.style.display="none";}} />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ))}

      {historyTid && (() => {
        const activeCard = cardsIn.find(c => c._tid === historyTid);
        if (!activeCard?._priceInfo) return null;
        return (
          <PriceHistoryModal
            marketPriceInfo={activeCard._priceInfo}
            condition={activeCard.Condition}
            cardName={activeCard.CardName}
            onClose={()=>setHistoryTid(null)}
            S={S} T={T} />
        );
      })()}

      {(cardsOut.length>0||cardsIn.length>0) && (
        <div style={{
          background: dayMode ? "#f0fdf4" : "#0f2e1a",
          border: `0.5px solid ${dayMode?"#16a34a":"#166534"}`,
          borderRadius:12, padding:"12px 14px", marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
            <span style={{fontSize:15,color: dayMode?"#166534":"#86efac"}}>Cards going out</span>
            <span style={{fontSize:15,color:D.redTxt,fontWeight:600}}>{fmtT(totalOut)}</span>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
            <span style={{fontSize:15,color: dayMode?"#166534":"#86efac"}}>
              Trade credit ({effectiveRate===100?"straight":`${effectiveRate}%`})
            </span>
            <span style={{fontSize:15,fontWeight:600,
              color: dayMode?"#15803d":"#4ade80"}}>{fmtT(totalCredit)}</span>
          </div>
          <div style={{borderTop:`0.5px solid ${dayMode?"#bbf7d0":"#166534"}`,paddingTop:8,marginTop:4}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
              <span style={{fontSize:15,fontWeight:700,color: dayMode?"#166534":"#86efac"}}>Balance</span>
              <span style={{fontSize:18,fontWeight:700,
                color:balance>=0?(dayMode?"#15803d":"#4ade80"):D.redTxt}}>
                {balance>=0?`+${fmtT(balance)}`:`${fmtT(balance)}`}
              </span>
            </div>
            <div style={{fontSize:15,color:balance>=0?(dayMode?"#15803d":"#4ade80"):D.redTxt,textAlign:"right"}}>
              {balance>0
                ? "You come out ahead — or give change back to customer"
                : balance<0
                ? `Customer owes you ${fmtT(Math.abs(balance))} cash or more cards`
                : "Even trade ✓"}
            </div>
          </div>
        </div>
      )}

      <input ref={tradePhotoRef} type="file" accept="image/*" capture="environment"
        style={{display:"none"}}
        onChange={e=>{
          const file=e.target.files?.[0];
          if(!file)return;
          const reader = new FileReader();
          reader.onload = (ev) => {
            const img = new Image();
            img.onload = () => {
              const MAX_DIM = 1000;
              let { width, height } = img;
              if (width > MAX_DIM || height > MAX_DIM) {
                if (width > height) { height = Math.round(height * MAX_DIM / width); width = MAX_DIM; }
                else { width = Math.round(width * MAX_DIM / height); height = MAX_DIM; }
              }
              const canvas = document.createElement("canvas");
              canvas.width = width; canvas.height = height;
              const ctx = canvas.getContext("2d");
              ctx.drawImage(img, 0, 0, width, height);
              setTradePhoto(canvas.toDataURL("image/jpeg", 0.7));
            };
            img.onerror = () => toast("Couldn't read that photo — please try again", "error");
            img.src = ev.target.result;
          };
          reader.onerror = () => toast("Couldn't read that photo — please try again", "error");
          reader.readAsDataURL(file);
          e.target.value = "";
        }} />

      <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:12}}>
        <button
          onClick={()=>tradePhotoRef.current?.click()}
          style={{...S.btn("ghost"), padding:"10px 16px", fontSize:13, flexShrink:0}}>
          📷 {tradePhoto ? "Retake Photo" : "Add Trade Photo"}
        </button>
        {tradePhoto && (
          <img src={tradePhoto} alt="Trade" style={{width:44,height:44,objectFit:"cover",
            borderRadius:8, border:`1px solid ${T.border}`}} />
        )}
      </div>

      <button style={{...S.btn("primary"),width:"100%",padding:"14px",fontSize:14,background:"#3C66EE"}}
        onClick={()=>confirm(false)} disabled={saving||!cardsOut.length||!cardsIn.length}>
        {saving?"Processing…":"📦 Confirm Trade + Add to Inventory"}
      </button>
      <div style={{textAlign:"center", fontSize:12, color:T.textFaint, margin:"8px 0"}}>or</div>
      <button style={{...S.btn("primary"),width:"100%",padding:"14px",fontSize:14,background:"#15803d"}}
        onClick={()=>confirm(true)} disabled={saving||!cardsOut.length||!cardsIn.length}>
        {saving?"Processing…":"🎪 Confirm Trade + Add to Show"}
      </button>
      <div style={{fontSize:13,color:T.textFaint,textAlign:"center",marginTop:8}}>
        Cards in will be date-stamped {today}
      </div>
    </div>
  );
}

// ─── BUY TAB ──────────────────────────────────────────────────────────────────
function BuyTab({ show, today, setNames, allInventory, vendorId, isPaid, onAddInventoryItem, onAddShowItem, dayMode, S, T }) {
  const { toast, confirmAction } = useNotify();
  const [marketIntelData, setMarketIntelData] = useState(null);
  useEffect(() => {
    if (!isPaid) return;
    fetchAllMarketIntelReports(SHARED_MARKET_INTEL_VENDOR_ID, "product_name,set_name,report_month,rank,avg_price")
      .then(data => setMarketIntelData(data || []))
      .catch(() => setMarketIntelData([]));
  }, [isPaid]);
  const [cart, setCart]           = useState([]);
  const [activeTab, setActiveTab] = useState("card");
  const [form, setForm]           = useState({
    CardName:"", Set:"", Number:"", Type:"Single Card",
    GameType:"Pokemon", Rarity:"Rare", Condition:"Near Mint", Language:"English", Qty:1,
    AmountPaid:"", Rate:70, ListPrice:"", ImageURL:"",
    LorcanaInkColor:"", LorcanaFoilType:"Normal",
  });
  const [useCustomRate, setUseCustomRate] = useState(false);
  const [customRateVal, setCustomRateVal] = useState(""); 
  const [amountPaidTouched, setAmountPaidTouched] = useState(false);
  const blueTxt = dayMode ? "#1d4ed8" : "#60a5fa";
  const [sessionPhoto, setSessionPhoto] = useState("");
  const photoRef = useRef(null);
  const [uploadingCardImage, setUploadingCardImage] = useState(false);
  const cardImageRef = useRef(null);
  const [paymentType, setPaymentType]   = useState("Cash");
  const [refNumber, setRefNumber]       = useState("");
  const [saving, setSaving]             = useState(false);
  const [done, setDone]                 = useState(null);
  const [lightbox, setLightbox]         = useState(false);
  const [showScanner, setShowScanner]   = useState(false);
  const [showApiSearch, setShowApiSearch] = useState(false);
  const [showInvSearch, setShowInvSearch] = useState(false);
  const [invSearchQuery, setInvSearchQuery] = useState("");
  const [apiQuery, setApiQuery]         = useState("");
  const [apiResults, setApiResults]     = useState([]);
  const [apiSearching, setApiSearching] = useState(false);
  const [apiSearchPage, setApiSearchPage] = useState(1);
  const [apiHasMore, setApiHasMore] = useState(false);
  const [apiLoadingMore, setApiLoadingMore] = useState(false);
  const [marketPriceInfo, setMarketPriceInfo] = useState(null);
  const [showPriceHistory, setShowPriceHistory] = useState(false);
  const [marketPriceLoading, setMarketPriceLoading] = useState(false);

  const paid = parseFloat(form.AmountPaid)||0;
  const ms   = paid>0 ? minSell(paid) : 0;
  const set  = (k,v) => setForm(f=>({...f,[k]:v}));

  useEffect(() => {
    if (!isPaid || !showApiSearch || !apiQuery.trim()) { setApiResults([]); setApiHasMore(false); return; }
    let cancelled = false;
    setApiSearching(true);
    setApiSearchPage(1);
    const timer = setTimeout(async () => {
      if (activeTab === "sealed") {
        const { results, error } = await apitcgSearchSealed(form.GameType, apiQuery.trim(), 1);
        if (!cancelled) {
          setApiResults(results || []);
          setApiHasMore(false);
          setApiSearching(false);
        }
        return;
      }
      const numberMatch = apiQuery.trim().match(/^(.+)\s+([A-Z]{0,4}\d+(?:\/[A-Z]{0,4}\d+)?)$/i);
      const name = numberMatch ? numberMatch[1].trim() : apiQuery.trim();
      const number = numberMatch ? numberMatch[2] : undefined;
      const { results, pagination } = await unifiedCardSearch(form.GameType, name, number, 1);
      if (!cancelled) {
        setApiResults(results || []);
        setApiHasMore(!!pagination && pagination.page < pagination.total_pages);
        setApiSearching(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [apiQuery, showApiSearch, isPaid, activeTab]);

  const loadMoreBuyApiResults = async () => {
    const numberMatch = apiQuery.trim().match(/^(.+)\s+([A-Z]{0,4}\d+(?:\/[A-Z]{0,4}\d+)?)$/i);
    const name = numberMatch ? numberMatch[1].trim() : apiQuery.trim();
    const number = numberMatch ? numberMatch[2] : undefined;
    const nextPage = apiSearchPage + 1;
    setApiLoadingMore(true);
    const { results, pagination } = await unifiedCardSearch(form.GameType, name, number, nextPage);
    setApiResults(prev => [...prev, ...(results || [])]);
    setApiHasMore(!!pagination && pagination.page < pagination.total_pages);
    setApiSearchPage(nextPage);
    setApiLoadingMore(false);
  };

  const selectApiCard = (result) => {
    setForm(f => ({
      ...f,
      Type:     activeTab === "sealed" ? "Sealed Product" : "Single Card",
      CardName: result.cardName || f.CardName,
      Set:      result.setName  || f.Set,
      Number:   result.cardNumber || f.Number,
      Rarity:   result.rarity   || f.Rarity,
      ImageURL: result.imageUrl || f.ImageURL,
      _tcggoId: result.tcggoId || null,
      _matched: true,
    }));
    setShowApiSearch(false); setApiQuery(""); setApiResults([]);
    setAmountPaidTouched(false);
  };

  const invSearchResults = useMemo(() => {
    if (!invSearchQuery.trim()) return [];
    const q = invSearchQuery.toLowerCase();
    return allInventory.filter(i =>
      i.Status !== "Sold" && i.Status !== "Deleted" &&
      i.CardName?.toLowerCase().includes(q)
    ).slice(0, 10);
  }, [invSearchQuery, allInventory]);

  const selectInvCard = (item) => {
    setForm(f => ({
      ...f,
      CardName: item.CardName || f.CardName,
      Set:      item.Set      || f.Set,
      Number:   item.Number   || f.Number,
      Rarity:   item.Rarity   || f.Rarity,
      GameType: item.GameType || f.GameType,
      ImageURL: item.ImageURL || f.ImageURL,
      _matched: true,
    }));
    setShowInvSearch(false); setInvSearchQuery("");
  };

  const cartTotal = cart.reduce((s,c)=>s+(parseFloat(c.AmountPaid)||0),0);

  useEffect(() => {
    if (!form.CardName?.trim()) { setMarketPriceInfo(null); return; }
    if (form.Type === "Sealed Product") {
      let cancelled = false;
      setMarketPriceLoading(true);
      const timer = setTimeout(async () => {
        const result = await apitcgLookupSealed(form.GameType, form.CardName, form._tcggoId);
        if (!cancelled) { setMarketPriceInfo(result); setMarketPriceLoading(false); }
      }, 500);
      return () => { cancelled = true; clearTimeout(timer); };
    }
    if (!form.Number?.trim()) { setMarketPriceInfo(null); return; }
    let cancelled = false;
    setMarketPriceLoading(true);
    const timer = setTimeout(async () => {
      const result = await unifiedCardLookup(form.GameType, form.CardName, form.Number, form.Set, null, null, null, form.Condition);
      if (!cancelled) { setMarketPriceInfo(result); setMarketPriceLoading(false); }
    }, 500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.CardName, form.Number, form.Set, form.Condition, form.Type, form.GameType, form._tcggoId]);

  const buyDecision = useMemo(() => {
    if (form.GameType !== "Pokemon" || !marketPriceInfo?._priceData) return null;
    return evaluateBuyDecision({
      rows7d: marketPriceInfo._priceData.rows7d,
      rows30d: marketPriceInfo._priceData.rows30d,
      condition: form.Condition,
      dealerPrice: form.AmountPaid,
    });
  }, [marketPriceInfo, form.Condition, form.AmountPaid, form.GameType]);

  useEffect(() => {
    if (amountPaidTouched) return;
    const center = marketPriceInfo?.center;
    if (center == null) return;
    const rate = useCustomRate ? (parseFloat(customRateVal) || 0) : form.Rate;
    if (!rate) return;
    set("AmountPaid", String(Math.round(center * rate / 100)));
  }, [marketPriceInfo, form.Rate, useCustomRate, customRateVal, amountPaidTouched]);


  const addToCart = async () => {
    const nameKey = form.CardName;
    if (!nameKey || !paid) return;
    const qty = form.Type === "Sealed Product" ? Math.max(1, parseInt(form.Qty)||1) : 1;
    const perUnitCost = qty > 1 ? paid / qty : paid;
    const mv = marketPriceInfo?.center || 0;
    const perUnitMv   = qty > 1 ? mv   / qty : mv;
    const perUnitMs   = qty > 1 ? ms   / qty : ms;

    if (qty === 1) {
      const dupeCount = cart.filter(c =>
        c.CardName.trim().toLowerCase() === nameKey.trim().toLowerCase()
      ).length;
      if (dupeCount > 0) {
        if (!await confirmAction(`"${nameKey}" is already in the cart ${dupeCount} time${dupeCount>1?"s":""}. Add another copy?`, {confirmLabel:"Add Copy", danger:false})) return;
      }
    }

    if (qty > 1) {
      const newItems = Array.from({length: qty}, (_,i) => ({
        ...form,
        AmountPaid: String(perUnitCost.toFixed(2)),
        ListPrice: String(Math.round(perUnitMs)),
        _id: Date.now() + i,
        _mv: Math.round(perUnitMv),
        _ms: Math.round(perUnitMs),
        _qtyNote: `${i+1}/${qty}`,
      }));
      setCart(c=>[...c, ...newItems]);
    } else {
      setCart(c=>[...c, {
        ...form,
        ListPrice: String(Math.round(ms)),
        _id: Date.now(),
        _mv: Math.round(mv),
        _ms: Math.round(ms),
      }]);
    }
    setForm(f=>({...f, CardName:"", Number:"", AmountPaid:"", ListPrice:"", Qty:1, _matched:false}));
    setAmountPaidTouched(false);

    if (isPaid && marketIntelData?.length) {
      const analytics = computeCardAnalytics(marketIntelData, nameKey);
      if (analytics?.opportunity?.label === "AVOID") {
        toast(`⛔ Heads up: "${nameKey}" is flagged Avoid in Market Intel, ${PHASE_META[analytics.marketPhase]?.label || "declining demand"}.`, "alert");
      }
    }
  };

  const confirmAll = async (addToShow = false) => {
    if (cart.length === 0) return;
    setSaving(true);
    const photoNote = sessionPhoto ? ` · Photo: ${sessionPhoto}` : "";
    const payNote   = paymentType !== "Cash" ? ` · ${paymentType}${refNumber ? " #"+refNumber : ""}` : "";
    for (const card of cart) {
      const saved = await onAddInventoryItem({
        Type: card.Type, CardName: card.CardName, Set: card.Set,
        Number: card.Number, Rarity: card.Rarity, Language: card.Language,
        Condition: card.Condition, Grade:"", Quantity:1, Source:"Individual Buy",
        GameType: card.GameType || "Pokemon",
        LorcanaInkColor: card.GameType==="Lorcana" ? (card.LorcanaInkColor||"") : "",
        LorcanaFoilType: card.GameType==="Lorcana" ? (card.LorcanaFoilType||"Normal") : "Normal",
        AcquisitionCost: parseFloat(card.AmountPaid)||0,
        ListPrice: parseFloat(card.ListPrice)||"", MinSellPrice: card._ms,
        MarketValueAtAcquisition: card._mv,
        AcquisitionDate: today,
        Status: addToShow ? "Reserved" : "In Stock",
        ShowID: addToShow ? show.ID : "",
        ShowName: addToShow ? show.Name : "",
        AcquiredShowID: show.ID,
        LotID:"", LotName:"", LotItemNumber:"", ImageURL: card.ImageURL || "",
        Notes:`Purchased at ${show.Name}${payNote}${photoNote}`,
      });
      if (addToShow && saved?.ID && onAddShowItem) {
        await onAddShowItem(saved.ID);
      }
    }
    setDone({ count: cart.length, total: cartTotal, payment: paymentType,
              photo: sessionPhoto, addedToShow: addToShow });
    setCart([]); setSessionPhoto(""); setRefNumber(""); setSaving(false);
  };

  if (done) return (
    <div style={{padding:24, textAlign:"center"}}>
      <div style={{fontSize:36, marginBottom:12}}>✅</div>
      <div style={{fontSize:18, fontWeight:700, marginBottom:8}}>Purchase Complete</div>
      <div style={{fontSize:13, color:T.textMuted, marginBottom:8}}>
        {done.count} card{done.count!==1?"s":""} added · {fmtT(done.total)} total
      </div>
      {done.addedToShow && (
        <div style={{fontSize:13, color:T.green, fontWeight:600, marginBottom:8}}>
          Added to {show.Name} show inventory
        </div>
      )}
      <div style={{fontSize:13, color:T.textMuted, marginBottom:20}}>
        {done.payment}
        {done.photo && (
          <span style={{marginLeft:8, color:"#3b82f6", cursor:"pointer",
            textDecoration:"underline"}}
            onClick={()=>window.open(done.photo,"_blank")}>
            View photo
          </span>
        )}
      </div>
      <button style={{...S.btn("primary"), padding:"12px 24px"}}
        onClick={()=>setDone(null)}>New Purchase</button>
    </div>
  );

  return (
    <div style={{padding:16, maxWidth:"100%", boxSizing:"border-box",
      background: dayMode ? T.bg : "transparent", color: T.text, minHeight:"100%"}}>

      {showScanner && (
        <CardScanner
          S={S} T={T}
          mode="buy"
          onClose={() => setShowScanner(false)}
          onSelect={(r) => {
            setShowScanner(false);
            setForm(f => ({
              ...f,
              CardName: r.cardName || f.CardName,
              Number:   r.cardNumber || f.Number,
              Set:      r.setName   || f.Set,
              Rarity:   r.rarity    || f.Rarity,
            }));
          }}
        />
      )}
      {lightbox && sessionPhoto && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.9)",
          display:"flex",alignItems:"center",justifyContent:"center",zIndex:300}}
          onClick={()=>setLightbox(false)}>
          <img src={sessionPhoto} alt="Purchase"
            style={{maxWidth:"95vw",maxHeight:"90vh",objectFit:"contain",borderRadius:8}} />
        </div>
      )}

      {cart.length > 0 && (
        <div style={{...S.infoBox, marginBottom:12, borderColor:"#1d4ed8"}}>
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"center",marginBottom:6}}>
            <div style={{fontSize:13,fontWeight:600}}>
              {cart.length} card{cart.length!==1?"s":""} in cart
            </div>
            <div style={{fontSize:16,fontWeight:700,color:T.text}}>
              {fmtT(cartTotal)} total
            </div>
          </div>
          {cart.map((c,i)=>(
            <div key={c._id} style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",padding:"4px 0",
              borderTop:`0.5px solid ${T.border}`}}>
              <div style={{fontSize:12,flex:1,minWidth:0,overflow:"hidden",
                textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {c.CardName}{c.Set?` · ${c.Set}`:""}
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                <span style={{fontSize:15,color:"#4ade80",fontWeight:600}}>{fmtT(c.AmountPaid)}</span>
                <span style={{fontSize:15,color:T.textFaint}}>|</span>
                <span style={{fontSize:15,color:"#ffffff",fontWeight:700}}>{fmtT(c._ms||0)}</span>
                <span style={{cursor:"pointer",color:T.textMuted,fontSize:14}}
                  onClick={()=>setCart(p=>p.filter((_,j)=>j!==i))}>×</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{
        background: dayMode ? "#fff" : T.surface2,
        border: `0.5px solid ${dayMode?"#ccc":T.border}`,
        borderRadius:14, padding:16, marginBottom:12}}>

        <div style={{fontSize:16, fontWeight:700, marginBottom:12,
          color: dayMode?"#111":T.text}}>
          {cart.length === 0 ? "Add cards from this seller" : "Add another card"}
        </div>

        <div style={{marginBottom:14}}>
          <label style={{fontSize:13, fontWeight:600, color: dayMode?"#555":T.textMuted,
            display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.05em"}}>Choose a Game</label>
          <select style={S.select}
            value={form.GameType}
            onChange={e=>{
              const g = e.target.value;
              set("GameType",g);
              set("Set","");
              if (g==="Pokemon") set("Language","English");
            }}>
            {["Pokemon","Lorcana","Magic","One Piece","Yu-Gi-Oh"].map(g=><option key={g} value={g}>{g}</option>)}
          </select>
        </div>

        <div style={{display:"flex", gap:6, marginBottom:14}}>
          {[["card","Card"],["sealed","Sealed"]].map(([tab,label])=>(
            <button key={tab}
              style={{flex:1, textAlign:"center", padding:"10px 12px", borderRadius:10, cursor:"pointer",
                border:`1px solid ${activeTab===tab ? "#3C66EE" : (dayMode?"#ccc":T.border)}`,
                background: activeTab===tab ? "rgba(60,102,238,0.12)" : "transparent",
                color: activeTab===tab ? blueTxt : (dayMode?"#666":T.textMuted),
                fontSize:13, fontWeight:600}}
              onClick={()=>{
                setActiveTab(tab);
                set("Type", tab==="sealed" ? "Sealed Product" : "Single Card");
                setApiQuery(""); setApiResults([]);
              }}>
              {label}
            </button>
          ))}
        </div>

        {isPaid && (
          <>
            <button onClick={()=>{ setShowApiSearch(s=>!s); setShowInvSearch(false); }}
              style={{width:"100%", padding:"14px", marginBottom:showApiSearch?8:10, borderRadius:12,
                border:"none", background:"#3C66EE", color:"#fff",
                fontSize:16, fontWeight:700, cursor:"pointer"}}>
              🔍 {showApiSearch ? "Close Search" : "Search Market Price"}
            </button>
            {showApiSearch && (
              <div style={{marginBottom:14}}>
                <input style={{...S.input, marginBottom:8}} value={apiQuery}
                  onChange={e=>setApiQuery(e.target.value)}
                  placeholder="Type a card name…" autoFocus />
                {apiSearching && (
                  <div style={{fontSize:12, color:T.textFaint, marginBottom:6}}>Searching…</div>
                )}
                {apiResults.map((item,i)=>(
                  <div key={item.tcggoId||i}
                    style={{background: dayMode?"#fff":"#1e1e30",
                      border:`0.5px solid ${dayMode?"#ccc":T.border}`,
                      borderRadius:10, padding:8, marginBottom:6,
                      cursor:"pointer", display:"flex", gap:10, alignItems:"center"}}
                    onClick={()=>selectApiCard(item)}>
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt="" style={{width:60,height:84,objectFit:"cover",borderRadius:6,flexShrink:0}}/>
                    ) : (
                      <div style={{width:60,height:84,borderRadius:6,flexShrink:0,
                        background: dayMode?"#e4e4dc":"#2a2a40",
                        display:"flex",alignItems:"center",justifyContent:"center",
                        fontSize:14, color: dayMode?"#bbb":"#444"}}>⬡</div>
                    )}
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14,fontWeight:600,color: dayMode?"#111":T.text}}>{item.cardName}</div>
                      <div style={{fontSize:13,color: dayMode?"#555":T.textMuted}}>
                        {item.setName}{item.cardNumber ? ` · #${item.cardNumber}` : ""}
                      </div>
                    </div>
                  </div>
                ))}
                {apiHasMore && (
                  <div style={{textAlign:"center", marginTop:4, marginBottom:8}}>
                    <span style={{fontSize:13, fontWeight:600, color:blueTxt, cursor:"pointer"}}
                      onClick={loadMoreBuyApiResults}>
                      {apiLoadingMore ? "Loading…" : "More results"}
                    </span>
                  </div>
                )}
              </div>
            )}
            <div style={{textAlign:"center", fontSize:12, color:T.textFaint, marginBottom:10}}>or</div>
          </>
        )}

        <button onClick={()=>{ setShowInvSearch(s=>!s); setShowApiSearch(false); }}
          style={{width:"100%", padding:"14px", marginBottom:showInvSearch?8:14, borderRadius:12,
            border:`1px solid ${dayMode?"#ccc":T.border}`, background:"transparent",
            color: dayMode?"#333":T.text,
            fontSize:16, fontWeight:700, cursor:"pointer"}}>
          ✍️ {showInvSearch ? "Close" : "Add Manually"}
        </button>
        {showInvSearch && (
          <div style={{marginBottom:14}}>
            <input style={{...S.input, marginBottom:8}} value={invSearchQuery}
              onChange={e=>setInvSearchQuery(e.target.value)}
              placeholder="Type a card name…" autoFocus />
            <div style={{fontSize:11, color:T.textFaint, marginBottom:8}}>
              Autofills from your existing inventory if this card is already in it
            </div>
            {invSearchResults.map(item=>(
              <div key={item.ID}
                style={{background: dayMode?"#fff":"#1e1e30",
                  border:`0.5px solid ${dayMode?"#ccc":T.border}`,
                  borderRadius:10, padding:8, marginBottom:6,
                  cursor:"pointer", display:"flex", gap:10, alignItems:"center"}}
                onClick={()=>selectInvCard(item)}>
                {item.ImageURL ? (
                  <img src={item.ImageURL} alt="" style={{width:60,height:84,objectFit:"cover",borderRadius:6,flexShrink:0}}/>
                ) : (
                  <div style={{width:60,height:84,borderRadius:6,flexShrink:0,
                    background: dayMode?"#e4e4dc":"#2a2a40",
                    display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:14, color: dayMode?"#bbb":"#444"}}>⬡</div>
                )}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:14,fontWeight:600,color: dayMode?"#111":T.text}}>{item.CardName}</div>
                  <div style={{fontSize:13,color: dayMode?"#555":T.textMuted}}>{item.Set}</div>
                </div>
              </div>
            ))}
            {invSearchQuery.trim() && invSearchResults.length === 0 && (
              <div style={{fontSize:12, color:T.textFaint}}>
                No match in your inventory, fill in the fields below to add it as new.
              </div>
            )}
          </div>
        )}

        <div style={{marginBottom:12}}>
          <label style={{fontSize:13, fontWeight:600, color: dayMode?"#555":T.textMuted,
            display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.05em"}}>
            {form.Type==="Sealed Product" ? "Product Name *" : "Card Name *"}
          </label>
          <div style={{display:"flex", gap:8}}>
            <input
              style={{flex:1, fontSize:17, padding:"11px 14px", borderRadius:10,
                background: dayMode?"#f8f8f5":T.surface,
                border:`1px solid ${dayMode?"#ccc":T.border}`,
                color: dayMode?"#111":T.text, fontWeight:500}}
              value={form.CardName}
              onChange={e=>set("CardName",e.target.value)}
              placeholder={form.Type==="Sealed Product" ? "e.g. Elite Trainer Box" : "e.g. Charizard ex"} />
          </div>
        </div>

        {(marketPriceLoading || marketPriceInfo) && (
          <div style={{textAlign:"center", marginBottom:14, padding:"14px 12px", borderRadius:12,
            background: dayMode?"#eff6ff":"rgba(60,102,238,0.10)",
            border:`1px solid ${dayMode?"#93c5fd":"rgba(60,102,238,0.3)"}`}}>
            {marketPriceLoading ? (
              <div style={{fontSize:14, color: dayMode?"#555":T.textMuted}}>Looking up market price…</div>
            ) : marketPriceInfo?.center != null ? (
              <>
                <div style={{fontSize:32, fontWeight:800, lineHeight:1.1,
                  color: dayMode?"#1d4ed8":"#88bdf2", fontFamily:"'DM Mono',monospace"}}>
                  {fmtT(marketPriceInfo.center)}
                </div>
                <div style={{fontSize:13, color: dayMode?"#666":T.textMuted, marginTop:4}}>
                  {marketPriceInfo._noConditionPricing
                    ? (form.Type==="Sealed Product" ? "Market Price" : "Market Price · Near Mint")
                    : <>Range {fmtT(marketPriceInfo.low)} – {fmtT(marketPriceInfo.high)} · {form.Condition}</>}
                </div>
                {marketPriceInfo.asOfDate && marketPriceInfo.asOfDate !== today && (
                  <div style={{fontSize:11, color: dayMode?"#999":T.textFaint, marginTop:2}}>
                    Prices as of {marketPriceInfo.asOfDate}
                  </div>
                )}
                {(marketPriceInfo._priceData || marketPriceInfo._rawRows) && (
                  <div style={{fontSize:12, fontWeight:700, color:blueTxt, marginTop:8, cursor:"pointer"}}
                    onClick={()=>setShowPriceHistory(true)}>
                    View History
                  </div>
                )}
                {buyDecision && buyDecision.verdict !== "enter_price" && buyDecision.verdict !== "insufficient_data" && (
                  <div style={{marginTop:12, padding:"8px 12px", borderRadius:8,
                    background: buyDecision.verdict==="buy" ? "rgba(74,222,128,0.12)"
                      : buyDecision.verdict==="caution" ? "rgba(251,191,36,0.12)" : "rgba(248,113,113,0.12)"}}>
                    <div style={{fontSize:14, fontWeight:800,
                      color: buyDecision.verdict==="buy" ? "#4ade80"
                        : buyDecision.verdict==="caution" ? "#fbbf24" : "#f87171"}}>
                      {buyDecision.verdict==="buy" ? "✓ BUY" : buyDecision.verdict==="caution" ? "⚠ CAUTION" : "✗ PASS"}
                    </div>
                    {buyDecision.reasons.map((r,i) => (
                      <div key={i} style={{fontSize:11, color: dayMode?"#666":T.textMuted, marginTop:2}}>{r}</div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div style={{fontSize:13, color: dayMode?"#888":T.textFaint}}>No market price found for this card</div>
            )}
          </div>
        )}

        {showPriceHistory && marketPriceInfo && (
          <PriceHistoryModal
            marketPriceInfo={marketPriceInfo}
            condition={form.Condition}
            cardName={form.CardName}
            onClose={()=>setShowPriceHistory(false)}
            S={S} T={T} />
        )}

        <div style={{marginBottom:12}}>
          <label style={{fontSize:13, fontWeight:600, color: dayMode?"#555":T.textMuted,
            display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.05em"}}>
            Card Image <span style={{textTransform:"none", fontWeight:400}}>(optional)</span>
          </label>
          <div style={{display:"flex", gap:8, alignItems:"center"}}>
            <input style={{flex:1, fontSize:14, padding:"9px 12px", borderRadius:10,
                background: dayMode?"#fff":T.surface,
                border:`1px solid ${dayMode?"#ccc":T.border}`,
                color: dayMode?"#111":T.text}}
              value={form.ImageURL||""} onChange={e=>set("ImageURL",e.target.value)}
              placeholder={uploadingCardImage ? "Uploading photo..." : (form._matched ? "Image URL" : "Paste a URL, or take a photo...")}
              disabled={uploadingCardImage} />
            {!form._matched && (
              <>
                <input ref={cardImageRef} type="file" accept="image/*" capture="environment"
                  style={{display:"none"}}
                  onChange={e=>{
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (!vendorId) { toast("Can't upload right now — please try again shortly", "error"); return; }
                    setUploadingCardImage(true);
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      const img = new Image();
                      img.onload = () => {
                        const MAX_DIM = 1000;
                        let { width, height } = img;
                        if (width > MAX_DIM || height > MAX_DIM) {
                          if (width > height) { height = Math.round(height * MAX_DIM / width); width = MAX_DIM; }
                          else { width = Math.round(width * MAX_DIM / height); height = MAX_DIM; }
                        }
                        const canvas = document.createElement("canvas");
                        canvas.width = width; canvas.height = height;
                        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
                        canvas.toBlob(async (blob) => {
                          try {
                            const url = await uploadCardImage(vendorId, blob);
                            set("ImageURL", url);
                          } catch(err) {
                            toast("Photo upload failed: " + err.message, "error");
                          }
                          setUploadingCardImage(false);
                        }, "image/jpeg", 0.8);
                      };
                      img.onerror = () => { toast("Couldn't read that photo — please try again", "error"); setUploadingCardImage(false); };
                      img.src = ev.target.result;
                    };
                    reader.onerror = () => { toast("Couldn't read that photo — please try again", "error"); setUploadingCardImage(false); };
                    reader.readAsDataURL(file);
                    e.target.value = "";
                  }} />
                <button style={{...S.btn("ghost"),padding:"10px 14px",flexShrink:0}}
                  disabled={uploadingCardImage}
                  onClick={()=>cardImageRef.current?.click()}>
                  {uploadingCardImage ? "…" : "📷"}
                </button>
              </>
            )}
            {form.ImageURL && !uploadingCardImage && (
              <img src={form.ImageURL} alt="" style={{width:60,height:84,objectFit:"cover",
                borderRadius:6,border:`1px solid ${T.border}`,flexShrink:0}}
                onError={e=>{e.target.style.display="none";}} />
            )}
          </div>
        </div>

        {form.GameType!=="Lorcana" && form.Type!=="Sealed Product" && (
          <div style={{display:"flex", gap:4, marginBottom:6}}>
            {[["EN","English"],["JP","Japanese"],["CN","Chinese"]].map(([code,lang])=>(
              <button key={code}
                style={{padding:"4px 8px", fontSize:11, fontWeight:600, borderRadius:6,
                  border:`1px solid ${(form.Language||"English")===lang?"#3C66EE":(dayMode?"#ccc":T.border)}`,
                  background:(form.Language||"English")===lang?"#3C66EE":"transparent",
                  color:(form.Language||"English")===lang?"#fff":(dayMode?"#444":T.textMuted),
                  cursor:"pointer"}}
                onClick={()=>{set("Language",lang);set("Set","");}}>
                {code}
              </button>
            ))}
          </div>
        )}

        {form._matched ? (
          <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:12,
            padding:"10px 12px", borderRadius:10,
            background: dayMode?"#f5f5f5":T.surface2,
            fontSize:13, color: dayMode?"#555":T.textMuted}}>
            ✓ Matched: {form.Set}{form.Number ? ` · #${form.Number}` : ""}
          </div>
        ) : (
        <div style={{display:"flex", gap:8, marginBottom:12, alignItems:"flex-end"}}>
          <div style={{flex:1}}>
            <label style={{fontSize:13, fontWeight:600, color: dayMode?"#555":T.textMuted,
              display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.05em"}}>Set</label>
            <select
              style={{width:"100%", fontSize:15, padding:"10px 12px", borderRadius:10,
                background: dayMode?"#fff":T.surface,
                border:`1px solid ${dayMode?"#ccc":T.border}`,
                color: dayMode?"#111":T.text, WebkitAppearance:"none"}}
              value={form.Set} onChange={e=>set("Set",e.target.value)}>
              <option value="">Select set...</option>
              {(
                form.GameType==="Lorcana" ? (CONFIG.LORCANA_SETS||[]) :
                form.GameType==="Magic" ? (CONFIG.MAGIC_SETS||[]) :
                form.GameType==="One Piece" ? (CONFIG.ONEPIECE_SETS||[]) :
                form.GameType==="Yu-Gi-Oh" ? (CONFIG.YUGIOH_SETS||[]) :
                (form.Language==="Japanese") ? (CONFIG.JAPANESE_SETS||[]).map(s=>({name:s})) :
                (form.Language==="Chinese")  ? (CONFIG.CHINESE_SETS||CONFIG.POKEMON_SETS).map(s=>typeof s==="string"?{name:s}:s) :
                CONFIG.POKEMON_SETS
              ).map(s=><option key={s.name||s} value={s.name||s}>{s.name||s}</option>)}
            </select>
          </div>
          {form.Type!=="Sealed Product" && (
          <div style={{width:80, flexShrink:0}}>
            <label style={{fontSize:13, fontWeight:600, color: dayMode?"#555":T.textMuted,
              display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.05em"}}>Card #</label>
            <input
              style={{width:"100%", fontSize:15, padding:"10px 6px", borderRadius:10,
                background: dayMode?"#fff":T.surface,
                border:`1px solid ${dayMode?"#ccc":T.border}`,
                color: dayMode?"#111":T.text, textAlign:"center", boxSizing:"border-box"}}
              value={form.Number} onChange={e=>set("Number",e.target.value)} placeholder="—" />
          </div>
          )}
        </div>
        )}

        {form.Type!=="Sealed Product" && (
        <div style={{marginBottom:12}}>
          <label style={{fontSize:13, fontWeight:600, color: dayMode?"#555":T.textMuted,
            display:"block", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.05em"}}>Condition</label>
          <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
            {[["Near Mint","NM"],["Lightly Played","LP"],["Moderately Played","MP"],["Heavily Played","HP"],["Damaged","DM"]].map(([full,abbr])=>(
              <button key={full}
                style={{padding:"9px 14px", fontSize:14, fontWeight:700, borderRadius:10,
                  border:`1px solid ${form.Condition===full?"#3C66EE":(dayMode?"#ccc":T.border)}`,
                  background: form.Condition===full ? "#3C66EE" : "transparent",
                  color: form.Condition===full ? "#fff" : (dayMode?"#333":T.textMuted),
                  cursor:"pointer"}}
                onClick={()=>set("Condition",full)}>{abbr}</button>
            ))}
          </div>
        </div>
        )}

        {form.Type==="Sealed Product" && (
          <div style={{marginBottom:12}}>
            <label style={{fontSize:13, fontWeight:600, color: dayMode?"#555":T.textMuted,
              display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.05em"}}>Qty</label>
            <input style={{fontSize:16, padding:"10px", borderRadius:10, width:80, textAlign:"center",
              background: dayMode?"#fff":T.surface,
              border:`1px solid ${dayMode?"#ccc":T.border}`,
              color: dayMode?"#111":T.text}}
              type="number" min="1"
              value={form.Qty} onChange={e=>set("Qty",Math.max(1,parseInt(e.target.value)||1))} />
          </div>
        )}

        <div style={{height:"0.5px", background: dayMode?"#ddd":T.border, margin:"12px 0"}}/>

        <div style={{display:"flex", gap:10, marginBottom:12}}>
          <div style={{flex:1}}>
            <label style={{fontSize:13, fontWeight:600, color: dayMode?"#555":T.textMuted,
              display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.05em"}}>
              Amount Paid *
            </label>
            <input
              style={{width:"100%", fontSize:24, fontWeight:700, textAlign:"center",
                padding:"12px 8px", borderRadius:10,
                background: dayMode?"#fff":T.surface,
                border:`1px solid ${dayMode?"#ccc":T.border}`,
                color: dayMode?"#111":T.text}}
              type="number" min="0" step="0.01"
              value={form.AmountPaid}
              onChange={e=>{ setAmountPaidTouched(true); set("AmountPaid",e.target.value); }} placeholder="$0" />
          </div>
          <div style={{flex:1}}>
            <label style={{fontSize:13, fontWeight:600, color: dayMode?"#555":T.textMuted,
              display:"block", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.05em"}}>
              List Price
            </label>
            <input
              style={{width:"100%", fontSize:24, fontWeight:700, textAlign:"center",
                padding:"12px 8px", borderRadius:10,
                background: dayMode?"#f0fdf4":"#0f2e1a",
                border:`1px solid ${dayMode?"#16a34a":"#22c55e"}`,
                color: dayMode?"#15803d":"#4ade80"}}
              type="number" min="0" step="0.01"
              value={form.ListPrice}
              onChange={e=>set("ListPrice",e.target.value)} placeholder="$0" />
          </div>
        </div>

        <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:12}}>
          <label style={{fontSize:13, fontWeight:600, flexShrink:0,
            color: dayMode?"#333":T.textMuted, textTransform:"uppercase", letterSpacing:"0.05em"}}>
            Offer %
          </label>
          <select
            style={{flex:1, background: dayMode?"#fff":"#252535",
              border:`0.5px solid ${dayMode?"#bbb":"#444"}`,
              borderRadius:10, color: dayMode?"#111":"#fff",
              fontSize:15, padding:"10px 12px", WebkitAppearance:"none"}}
            value={useCustomRate ? "custom" : String(form.Rate)}
            onChange={e=>{
              if (e.target.value==="custom") { setUseCustomRate(true); setAmountPaidTouched(false); }
              else { setUseCustomRate(false); setCustomRateVal(""); set("Rate",parseInt(e.target.value)); setAmountPaidTouched(false); }
            }}>
            <option value="90">90% of market</option>
            <option value="85">85% of market</option>
            <option value="80">80% of market</option>
            <option value="75">75% of market</option>
            <option value="70">70% of market</option>
            <option value="60">60% of market</option>
            <option value="50">50% of market</option>
            <option value="custom">Custom %</option>
          </select>
          {useCustomRate && (
            <input
              style={{width:72, background: dayMode?"#fff":"#252535",
                border:"1.5px solid #3C66EE", borderRadius:10,
                color: dayMode?"#111":"#fff", fontSize:15,
                padding:"10px 8px", textAlign:"center"}}
              type="number" min="0" max="100" placeholder="%"
              value={customRateVal}
              onChange={e=>{ setCustomRateVal(e.target.value); set("Rate",parseFloat(e.target.value)||70); setAmountPaidTouched(false); }}
            />
          )}
        </div>

        {paid>0 && (
          <div style={{
            background: dayMode?"#f0fdf4":"#0f2e1a",
            border:`0.5px solid ${dayMode?"#16a34a":"#166634"}`,
            borderRadius:12, padding:"12px 14px", marginBottom:12}}>
            <div style={{fontSize:13, color: dayMode?"#166534":"#86efac", marginBottom:4}}>
              Your offer ({useCustomRate ? (parseFloat(customRateVal)||0) : form.Rate}% of market)
            </div>
            <div style={{fontSize:32, fontWeight:700,
              color: dayMode?"#15803d":"#4ade80",
              fontFamily:"'DM Mono',monospace"}}>
              {fmtT(paid)}
            </div>
            <div style={{marginTop:8}}>
              <div style={{fontSize:12, color: dayMode?"#555":"#666"}}>Min sell</div>
              <div style={{fontSize:15, fontWeight:600, color: dayMode?"#15803d":"#4ade80"}}>${ms}</div>
            </div>
          </div>
        )}

        {form.Qty > 1 && (
          <div style={{fontSize:13, color:T.textMuted, marginBottom:10,
            background:T.surface2, borderRadius:8, padding:"8px 12px"}}>
            Total split equally — each unit at ${paid > 0 ? (paid/form.Qty).toFixed(2) : "0.00"}
          </div>
        )}

        <button
          style={{background:"#3C66EE", border:"none", borderRadius:12,
            color:"#fff", fontSize:16, fontWeight:700, padding:"14px",
            width:"100%", cursor:"pointer",
            opacity:(!form.CardName||!paid)?0.5:1}}
          onClick={addToCart}
          disabled={!form.CardName||!paid}>
          {form.Type==="Sealed Product" && form.Qty > 1
            ? `+ Add ${form.Qty} Units to Cart`
            : "+ Add to Cart"}
        </button>
      </div>

      <div style={{marginBottom:12}}>
        <label style={S.label}>
          Transaction Photo <span style={{color:T.textFaint}}>(optional — shared across all cards in this purchase)</span>
        </label>
        <div style={{display:"flex",gap:8}}>
          <input style={{...S.input,flex:1,fontSize:13}} value={sessionPhoto}
            onChange={e=>setSessionPhoto(e.target.value)}
            placeholder="Paste URL or take photo..." />
          <input ref={photoRef} type="file" accept="image/*" capture="environment"
            style={{display:"none"}}
            onChange={e=>{
              const file=e.target.files?.[0];
              if(!file)return;
              const reader = new FileReader();
              reader.onload = (ev) => {
                const img = new Image();
                img.onload = () => {
                  const MAX_DIM = 1000;
                  let { width, height } = img;
                  if (width > MAX_DIM || height > MAX_DIM) {
                    if (width > height) { height = Math.round(height * MAX_DIM / width); width = MAX_DIM; }
                    else { width = Math.round(width * MAX_DIM / height); height = MAX_DIM; }
                  }
                  const canvas = document.createElement("canvas");
                  canvas.width = width; canvas.height = height;
                  const ctx = canvas.getContext("2d");
                  ctx.drawImage(img, 0, 0, width, height);
                  setSessionPhoto(canvas.toDataURL("image/jpeg", 0.7));
                };
                img.onerror = () => toast("Couldn't read that photo — please try again", "error");
                img.src = ev.target.result;
              };
              reader.onerror = () => toast("Couldn't read that photo — please try again", "error");
              reader.readAsDataURL(file);
              e.target.value = "";
            }} />
          <button style={{...S.btn("ghost"),padding:"10px 14px",flexShrink:0}}
            onClick={()=>photoRef.current?.click()}>📷</button>
        </div>
        {sessionPhoto && (
          <img src={sessionPhoto} alt="Transaction"
            style={{marginTop:8,width:"100%",maxHeight:160,objectFit:"cover",
              borderRadius:8,border:`1px solid ${T.border}`,cursor:"pointer"}}
            onClick={()=>setLightbox(true)} />
        )}
      </div>

      {cart.length > 0 && (
        <>
          <div style={{marginBottom:8}}>
            <label style={S.label}>Payment Method</label>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {PAYMENT_TYPES.map(pt=>(
                <button key={pt}
                  style={{...S.chip(paymentType===pt),flex:1,textAlign:"center",
                    padding:"8px 4px",fontSize:12,minWidth:56}}
                  onClick={()=>setPaymentType(pt)}>{pt}</button>
              ))}
            </div>
          </div>

          {paymentType !== "Cash" && (
            <div style={{marginBottom:12}}>
              <label style={S.label}>Reference Number</label>
              <input style={{...S.input,fontSize:14}} value={refNumber}
                onChange={e=>setRefNumber(e.target.value)}
                placeholder={`${paymentType} confirmation #`} />
            </div>
          )}

          <button style={{...S.btn("primary"),width:"100%",padding:"14px",
            fontSize:15,marginTop:4}}
            onClick={()=>confirmAll(false)} disabled={saving}>
            {saving?"Saving…":`Buy ${cart.length} Card${cart.length!==1?"s":""} · ${fmtT(cartTotal)}`}
          </button>
          <div style={{textAlign:"center", fontSize:12, color:T.textMuted,
            margin:"8px 0", fontWeight:600}}>— or —</div>
          <button style={{...S.btn("primary"),width:"100%",padding:"14px",
            fontSize:15, background:"#1a4a2a", border:"0.5px solid #2a6a3a"}}
            onClick={()=>confirmAll(true)} disabled={saving}>
            {saving?"Saving…":`Buy ${cart.length} Card${cart.length!==1?"s":""} · ${fmtT(cartTotal)} + Add to Show`}
          </button>
        </>
      )}
    </div>
  );
}
